import { GasMeterBrand, GasMeterDetails, GasMeterStatus, GasMeterType, GasMeterTypeBrandRelation } from './../models/gas-meter-catalog.interface';
import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, catchError, delay, firstValueFrom, forkJoin, from, map, mergeMap, of, switchMap, throwError, timer } from 'rxjs';
import { CommonService } from './common.service';
import { Building } from '../models/building.model';
import { Settlement, SettlementFiscalAttributes, SettlementFiscalPayload } from '../models/settlement.model';
import { SettlementContractHistory } from '../models/settlement-contract.model';
import { User } from '../models/user';
import { Utility } from '../models/utility.model';
import { InventoryCreditConfiguration, TankPricePreview, Tanque } from '../models/tanque.model';
import { PurchaseOrder } from '../models/purchase-order';
import { Order } from '../models/order.model';
import { Refuel } from '../models/refuel.model';
import { Tenant } from '../models/tenant.model';
import { Contract } from '../models/contract.model';
import { ContractType } from '../models/contract-type.model';
import { Permission } from '../models/permission.model';
import { Bill } from '../models/bill.model';
import { Role } from '../models/role.model';
import { BillItem } from '../models/bill-item.model';
import { Profile } from '../models/profile.model';
import { GasMeterRead } from '../models/gas-meter-read.model';
import { SettlementUser } from '../models/settlement-user.model';
import { Balance } from '../models/balance.model';
import { CalendarEventsRequest } from '../models/calendar-events-request.interface';
import { PeriodicityCategoriesResponse } from '../models/periodicity-categories-response.interface';
import { PeriodicityTypesResponse } from '../models/periodicity-types-response.interface';
import { InventoryAgreement } from '../models/inventory-agreement.interface';
import { CalendarParams } from '../models/calendar-params.interface';
import { CalendarEventsResponse, CalendarEventsResult, CalendarEventStatusCounts } from '../models/calendar-events-response.interface';
import { CALENDAR_EVENT_STATUS_NAMES, CalendarEvent, CalendarEventStatus, CalendarEventStatusUpdateResult, CalendarEventType } from '../models/calendar-event.interface';
import { DiscountType } from '../models/discount-type.model';
import { LegalEntity, LegalEntitiesResponse, legalEntityId } from '../models/legal-entity.model';
import {
  buildReceiptDiscountFields,
  resolveBoolean,
  resolveDiscountTypeId
} from '../shared/utils/discount-type.utils';
import { isBalanceCreditItem, withBalanceCreditLabel } from '../shared/utils/balance-credit.utils';
import { withReconnectionFeeLabel } from '../shared/utils/reconnection-fee.utils';
import { normalizeOptionalBoolean } from '../shared/utils/contract-term.utils';
import { resolveSettlementFiscalAttributes } from '../shared/utils/settlement-fiscal.utils';
import { UnitOperationsState } from '../models/unit-operations-state.model';

export type InvoiceCsvQuery = 'query' | 'query_global' | 'query_generic';

@Injectable({
  providedIn: 'root'
})
export class SettlementsService extends CommonService {
  get token(): string | null {
    return sessionStorage.getItem('token');
  }

  constructor(
    private httpClient: HttpClient,
  ) {
    super(httpClient);
  }

  // CRUD Settlements
  getLegalEntities(isCommercial: boolean | null = null, page = 1): Observable<LegalEntity[]> {
    const params: any = { page, per_page: 100 };
    if (isCommercial !== null) params.is_commercial = isCommercial;
    return this.httpClient.get<LegalEntitiesResponse>(
      `${this.apiURL}/legal_entities?${this.parseQueryParams(params)}`, this.getHeaders(this.token)
    ).pipe(switchMap(response => {
      const next = response.meta?.pagination?.next_page;
      return next && next > page
        ? this.getLegalEntities(isCommercial, next).pipe(map(rest => [...response.data, ...rest]))
        : of(response.data);
    }));
  }

  getSettlements(data: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.get(`${this.apiURL}/settlements?${this.parseQueryParams(data)}`, options)
      .pipe(catchError(this.errorHandler))
      .pipe(map((settlementsData: any) => {
        const settlements: Settlement[] = [];
        settlementsData.data?.forEach((settlementData: any) => {
          const settlement: Settlement = {
            id: settlementData?.id,
            uid: settlementData?.attributes?.uid,
            legal_entity: settlementData?.attributes?.legal_entity ?? null,
            legal_entity_id: legalEntityId(settlementData?.attributes?.legal_entity),
            name: settlementData?.attributes?.name,
            address: settlementData?.attributes?.address,
            phone: settlementData?.attributes?.phone,
            email: settlementData?.attributes?.email,
            website: settlementData?.attributes?.website,
            description: settlementData?.attributes?.description,
            status: settlementData?.attributes?.status,
            enabled: normalizeOptionalBoolean(settlementData?.attributes?.enabled),
            slug: settlementData?.attributes?.slug,
            country: settlementData?.attributes?.country,
            city: settlementData?.attributes?.city,
            state: settlementData?.attributes?.state,
            zip: settlementData?.attributes?.zip,
            latitude: settlementData?.attributes?.latitude,
            longitude: settlementData?.attributes?.longitude,
            created_at: settlementData?.attributes?.created_at,
            updated_at: settlementData?.attributes?.updated_at,
            tipo: settlementData?.attributes?.tipo,
            factor: settlementData?.attributes?.factor,
            admin_price: settlementData?.attributes?.admin_price,
            discount: settlementData?.attributes?.discount,
            discount_type_id: resolveDiscountTypeId(
              settlementData?.attributes?.discount_type_id ?? settlementData?.attributes?.discount_type
            ),
            show_discount_on_receipt: resolveBoolean(settlementData?.attributes?.show_discount_on_receipt),
            interests: settlementData?.attributes?.interests,
            invoice_limit: settlementData?.attributes?.invoice_limit,
            reconexion_fee: settlementData?.attributes?.reconexion_fee,
            utility_id: settlementData?.attributes?.utility_id,
            contract_term_years: settlementData?.attributes?.contract_term_years,
            automatic_renewal: normalizeOptionalBoolean(settlementData?.attributes?.automatic_renewal),
            contract_end_date: settlementData?.attributes?.contract_end_date,
            zone_id: settlementData?.attributes?.zone_id,
            cycle_day: settlementData?.attributes?.cycle_day,
            periodicity: settlementData?.attributes?.periodicity,
            admin_name: settlementData?.attributes?.admin_name,
            credit: settlementData?.attributes?.credit,
            corte: settlementData?.attributes?.corte,
            tampering_fine: settlementData?.attributes?.tampering_fine,
            merchant_id: settlementData?.attributes?.merchant_id,
            state_id: settlementData?.attributes?.state_id,
            outer_number: settlementData?.attributes?.outer_number,
            region_id: settlementData?.attributes?.region_id,
            image: settlementData?.attributes?.image,
            utility: settlementData?.attributes?.utility,
            buildings: settlementData?.attributes?.buildings,
            units: settlementData?.attributes?.units,
            published: settlementData?.attributes?.published,
            zone: settlementData?.attributes?.zone,
            tanques: settlementData?.attributes?.tanques,
            hide_factor: settlementData?.attributes?.hide_factor,
            settlement_fiscal_attributes: resolveSettlementFiscalAttributes(settlementData?.attributes),
          }
          settlements.push(settlement);
        });
        const meta: any = {
          per_page: settlementsData?.meta?.params?.per_page,
          total_entries: settlementsData?.meta?.pagination?.total_entries,
          current_page: settlementsData?.meta?.pagination?.current_page,
        };
        return { data: settlements, meta: meta };
      }));
  }

  getPeriodicityList(): Observable<PeriodicityCategoriesResponse> {
    const options = this.getHeaders(this.token);
    return this.http.get<PeriodicityCategoriesResponse>(`${this.apiURL}/periodicity_categories`, options);
  }

  getPeriodicityTypes(periodicityCategoryId?: number): Observable<PeriodicityTypesResponse> {
    const options = this.getHeaders(this.token);
    if (!periodicityCategoryId) {
      return this.http.get<PeriodicityTypesResponse>(`${this.apiURL}/periodicity_types`, options);
    }
    return this.http.get<PeriodicityTypesResponse>(`${this.apiURL}/periodicity_types`, {
      ...options,
      params: { periodicity_category_id: periodicityCategoryId }
    });
  }

  getDiscountTypes(): Observable<DiscountType[]> {
    const options = this.getHeaders(this.token);
    return this.httpClient.get<DiscountType[]>(`${this.apiURL}/discount_types`, options);
  }

  createSettlement(data: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.post(`${this.apiURL}/settlements`, data, options);
  }

  getSettlement(id: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.get(`${this.apiURL}/settlements/${id}`, options)
    .pipe(catchError(this.errorHandler))
    .pipe(map((data: any) => {      
      const settlementData = data.data;
      const settlement: Settlement = {
        id: settlementData?.id,
        uid: settlementData?.attributes?.uid,
        legal_entity: settlementData?.attributes?.legal_entity ?? null,
        legal_entity_id: legalEntityId(settlementData?.attributes?.legal_entity),
        name: settlementData?.attributes?.name,
        address: settlementData?.attributes?.address,
        phone: settlementData?.attributes?.phone,
        email: settlementData?.attributes?.email,
        website: settlementData?.attributes?.website,
        description: settlementData?.attributes?.description,
        status: settlementData?.attributes?.status,
        enabled: normalizeOptionalBoolean(settlementData?.attributes?.enabled),
        slug: settlementData?.attributes?.slug,
        code: settlementData?.attributes?.code,
        country: settlementData?.attributes?.country,
        city: settlementData?.attributes?.city,
        suburb: settlementData?.attributes?.suburb,
        suburb_type: settlementData?.attributes?.suburb_type,
        municipality: settlementData?.attributes?.municipality,
        state: settlementData?.attributes?.state,
        zip: settlementData?.attributes?.zip,
        latitude: settlementData?.attributes?.latitude,
        longitude: settlementData?.attributes?.longitude,
        created_at: settlementData?.attributes?.created_at,
        updated_at: settlementData?.attributes?.updated_at,
        tipo: settlementData?.attributes?.tipo,
        factor: settlementData?.attributes?.factor,
        admin_price: settlementData?.attributes?.admin_price,
        discount: settlementData?.attributes?.discount,
        discount_type_id: resolveDiscountTypeId(
          settlementData?.attributes?.discount_type_id ?? settlementData?.attributes?.discount_type
        ),
        show_discount_on_receipt: resolveBoolean(settlementData?.attributes?.show_discount_on_receipt),
        interests: settlementData?.attributes?.interests,
        invoice_limit: settlementData?.attributes?.invoice_limit,
        reconexion_fee: settlementData?.attributes?.reconexion_fee,
        utility_id: settlementData?.attributes?.utility_id,
        contrato: settlementData?.attributes?.contrato,
        vigencia: settlementData?.attributes?.vigencia,
        contract_term_years: settlementData?.attributes?.contract_term_years,
        automatic_renewal: normalizeOptionalBoolean(settlementData?.attributes?.automatic_renewal),
        contract_end_date: settlementData?.attributes?.contract_end_date,
        zone_id: settlementData?.attributes?.zone_id,
        cycle_day: settlementData?.attributes?.cycle_day,
        periodicity: settlementData?.attributes?.periodicity,
        admin_name: settlementData?.attributes?.admin_name,
        credit: settlementData?.attributes?.credit,
        corte: settlementData?.attributes?.corte,
        tampering_fine: settlementData?.attributes?.tampering_fine,
        merchant_id: settlementData?.attributes?.merchant_id,
        state_id: settlementData?.attributes?.state_id,
        outer_number: settlementData?.attributes?.outer_number,
        region_id: settlementData?.attributes?.region_id,
        published: settlementData?.attributes?.published,
        utility: settlementData?.attributes?.utility,
        buildings: settlementData?.attributes?.buildings,
        units: settlementData?.attributes?.units,
        zone: settlementData?.attributes?.zone,
        tanques: settlementData?.attributes?.tanques,
        hide_factor: settlementData?.attributes?.hide_factor,
        periodicity_configuration_attributes: settlementData?.attributes?.periodicity_configuration_attributes,
        settlement_fiscal_attributes: resolveSettlementFiscalAttributes(settlementData?.attributes)
      }
      return settlement;
    }
    ));
  }

  getSettlementNested(id: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.get(`${this.apiURL}/settlements/${id}/nested`, options);
  }

  getSettlementCalendar(params: any) {
    const options = this.getHeaders(this.token);
    return this.httpClient.get(`${this.apiURL}/settlements/calendar`, { params: params });
  }

  getCalendarEvents(params: Partial<CalendarParams>): Observable<CalendarEventsResult> {
    const options = this.getHeaders(this.token);
    return this.httpClient.get<CalendarEventsResponse>(`${this.apiURL}/calendar_events`, {
      ...options,
      params,
      observe: 'response'
    }).pipe(
      map(response => {
        const data = response.body?.data ?? [];
        const counts = CALENDAR_EVENT_STATUS_NAMES.reduce((result, statusName) => {
          const headerValue = response.headers.get(statusName);
          const parsedHeaderValue = headerValue === null ? Number.NaN : Number(headerValue);
          const bodyCount = data.filter(event => event.event_status?.name === statusName).length;
          result[statusName] = Number.isFinite(parsedHeaderValue)
            ? Math.max(parsedHeaderValue, bodyCount)
            : bodyCount;
          return result;
        }, {} as CalendarEventStatusCounts);

        return {
          data,
          meta: response.body?.meta,
          counts
        };
      })
    );
  }

  getCalendarEventStatuses(): Observable<CalendarEventStatus[]> {
    const options = this.getHeaders(this.token);
    return this.httpClient.get<CalendarEventStatus[]>(`${this.apiURL}/event_status`, options);
  }

  updateCalendarEventStatus(eventId: number, statusId: number): Observable<CalendarEvent> {
    const options = this.getHeaders(this.token);
    return this.httpClient.put<{ data: CalendarEvent }>(
      `${this.apiURL}/calendar_events/${eventId}`,
      { event_status: { id: statusId } },
      options
    ).pipe(map(response => response.data));
  }

  updateCalendarEventStatuses(
    eventIds: number[],
    statusId: number
  ): Observable<CalendarEventStatusUpdateResult> {
    return from(eventIds).pipe(
      mergeMap(eventId => this.updateCalendarEventStatus(eventId, statusId).pipe(
        map(event => ({
          eventId,
          success: true,
          event
        } as CalendarEventStatusUpdateResult)),
        catchError(error => of({
          eventId,
          success: false,
          error
        } as CalendarEventStatusUpdateResult))
      ), 5)
    );
  }

  unitHasStartOperationsDate(unit_id: number): Observable<CalendarEventsResponse> {
    const options = this.getHeaders(this.token);
    return this.httpClient.get<CalendarEventsResponse>(
      `${this.apiURL}/calendar_events?unit_id=${unit_id}&event_id=6&event_status_id=1`, options
    );
  }

  getUnitOperationsState(unitId: number): Observable<UnitOperationsState> {
    const validation = this.validateUnit(unitId).pipe(
      map((response: any): string[] => {
        if (response?.ok === true && Array.isArray(response.errors) && response.errors.length === 0) return [];
        if (response?.ok === false && Array.isArray(response.errors) && response.errors.length &&
          response.errors.every((item: unknown) => typeof item === 'string')) return response.errors;
        throw new Error('Invalid operations validation response');
      }),
      catchError(error => {
        const errors = error.error?.errors;
        return error.status >= 400 && error.status < 500 &&
          error.status !== 401 && error.status !== 403 &&
          Array.isArray(errors) && errors.length && errors.every(item => typeof item === 'string')
          ? of(errors as string[])
          : throwError(() => error);
      })
    );
    return forkJoin({ events: this.unitHasStartOperationsDate(unitId), errors: validation }).pipe(
      map(({ events, errors }): UnitOperationsState => {
        if (!Array.isArray(events.data)) throw new Error('Invalid operations start response');
        if (events.data.some(event => Number(event.unit?.id) !== unitId || Number(event.event?.id) !== 6)) {
          throw new Error('Operations start belongs to another unit or event type');
        }
        const completed = events.data.filter(event =>
          Number(event.unit?.id) === unitId && Number(event.event?.id) === 6 &&
          Number(event.event_status?.id) === 1
        );
        if (completed.length > 1 || Number(events.meta?.pagination?.total_count) > 1 ||
          events.meta?.pagination?.next_page || completed.some(event => !event.event_date)) {
          throw new Error('Ambiguous operations start response');
        }
        const startDate = completed[0]?.event_date ?? null;
        return {
          unitId,
          status: errors.length ? 'blocked' : startDate ? 'started' : 'ready',
          startDate,
          errors
        };
      }),
      catchError(() => of({ unitId, status: 'unavailable', startDate: null, errors: [] } as UnitOperationsState))
    );
  }

  validateUnit(unit_id: number) {
    const options = this.getHeaders(this.token);
    return this.httpClient.get(`${this.apiURL}/calendar_events/validate_unit?unit_id=${unit_id}`, options);
  }

  saveCalendarEvent(payload: CalendarEventsRequest) {
    const options = this.getHeaders(this.token);
    return this.httpClient.post(`${this.apiURL}/calendar_events`, payload, options);
  }

  getCalendarEventTypes(): Observable<CalendarEventType[]> {
    const options = this.getHeaders(this.token);
    return this.httpClient.get<CalendarEventType[]>(`${this.apiURL}/events`, options);
  }

  updateSettlement(id: any, data: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.put(`${this.apiURL}/settlements/${id}`, data, options);
  }

  createSettlementFiscal(
    settlementId: number,
    payload: SettlementFiscalPayload
  ): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.post(
      `${this.apiURL}/settlements/${settlementId}/settlement_fiscals`,
      payload,
      options
    );
  }

  updateSettlementFiscal(
    settlementId: number,
    fiscalId: number,
    payload: SettlementFiscalPayload
  ): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.put(
      `${this.apiURL}/settlements/${settlementId}/settlement_fiscals/${fiscalId}`,
      payload,
      options
    );
  }

  updateSettlementEnabled(settlementId: number, enabled: boolean): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.patch(
      `${this.apiURL}/settlements/${settlementId}/enabled`,
      { enabled },
      options
    );
  }
  updateSettlementImage(id: any, data: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.put(`${this.apiURL}/settlements/image/${id}`, data, options);
  }

  updateSettlementContrato(id: any, data: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.put(`${this.apiURL}/settlements/contrato/${id}`, data, options);
  }

  getSettlementContractHistory(id: number, page = 1, perPage = 20): Observable<SettlementContractHistory> {
    return this.httpClient.get<SettlementContractHistory>(`${this.apiURL}/settlements/${id}/contracts`, {
      ...this.getHeaders(this.token), params: { page, per_page: perPage }
    }).pipe(map(response => {
      if (!Array.isArray(response?.data) || !response.meta?.pagination
        || response.data.some(contract => !contract.id || Number(contract.settlement_id) !== Number(id))) {
        throw new Error('No se pudo leer el historial de contratos.');
      }
      return response;
    }));
  }

  uploadSettlementContract(id: number, file: File): Observable<unknown> {
    const body = new FormData();
    body.append('file', file, file.name);
    return this.httpClient.put(`${this.apiURL}/settlements/${id}/contracts`, body, this.getHeadersMultipart(this.token));
  }

  downloadSettlementContract(id: number, contractId: number): Observable<Blob> {
    return this.httpClient.get(`${this.apiURL}/settlements/${id}/contracts/${contractId}`, {
      headers: this.getHeadersMultipart(this.token).headers.set('Accept', 'application/octet-stream'),
      responseType: 'blob'
    }).pipe(switchMap(blob => from(blob.slice(0, 5).text()).pipe(map(signature => {
      if (signature !== '%PDF-') throw new Error('El archivo recibido no es un PDF válido.');
      return new Blob([blob], { type: 'application/pdf' });
    }))));
  }

  updateSettlementIdentificacion(id: any, data: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.put(`${this.apiURL}/settlements/identificacion/${id}`, data, options);
  }
  
  // CRUD Buildings
  getBuildings(data: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.get(`${this.apiURL}/buildings?${this.parseQueryParams(data)}`, options);
  }

  getBuilding(id: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.get(`${this.apiURL}/buildings/${id}`, options)
    .pipe(
      catchError(this.errorHandler)
    )
    .pipe(map((data: any) => {
      const buildingData = data.data;
      const building: Building = {
        id: buildingData?.id,
        name: buildingData?.attributes?.name,
        clave: buildingData?.attributes?.clave,
        settlement: buildingData?.attributes?.settlement,
        floors: buildingData?.attributes?.floors,
        utilities: buildingData?.attributes?.utilities,
        tanques: buildingData?.attributes?.tanques,
        updated_at: buildingData?.attributes?.updated_at,
        created_at: buildingData?.attributes?.created_at,
      }
      return building;
    }));
  }

  createBuilding(data: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.post(`${this.apiURL}/buildings`, data, options);
  }

  updateBuilding(id: any, data: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.put(`${this.apiURL}/buildings/${id}`, data, options);
  }

  destroyBuilding(id: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.delete(`${this.apiURL}/buildings/${id}`, options);
  }

  getUnitsBySettlement(id: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.get(`${this.apiURL}/settlements/${id}/units`, options);
  }

  getUnitsByBuilding(id: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.get(`${this.apiURL}/buildings/${id}/units`, options)
    .pipe(
      catchError(this.errorHandler)
    )
    .pipe(map((data: any) => {
      const unitsData = data.data;
      const units: any = [];
      unitsData.forEach((unit_serialized: any) => {
        const unit: any = {
          id: unit_serialized?.id,
          name: unit_serialized?.attributes?.name,
          clave: unit_serialized?.attributes?.clave,
          settlement: unit_serialized?.attributes?.settlement,
          floors: unit_serialized?.attributes?.floors,
          utilities: unit_serialized?.attributes?.utilities,
          tanques: unit_serialized?.attributes?.tanques,
          updated_at: unit_serialized?.attributes?.updated_at,
          created_at: unit_serialized?.attributes?.created_at,
          gas_meters: unit_serialized?.attributes?.gas_meters,
          tenants: unit_serialized?.attributes?.tenants,
        }
        units.push(unit);
      });
      return units;
    }
    ));
  }

  

  // CRUD Services

  getServices(): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.get(`${this.apiURL}/services`, options);
  }

  getService(id: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.get(`${this.apiURL}/services/${id}`, options);
  }

  createService(data: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.post(`${this.apiURL}/services`, data, options);
  }

  updateService(id: any, data: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.put(`${this.apiURL}/services/${id}`, data, options);
  }

  destroyService(id: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.delete(`${this.apiURL}/services/${id}`, options);
  }

  // CRUD Amenities

  getAmenities(data: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.get(`${this.apiURL}/amenities?${this.parseQueryParams(data)}`, options);
  }

  getAmenity(id: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.get(`${this.apiURL}/amenities/${id}`, options);
  }

  createAmenity(data: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.post(`${this.apiURL}/amenities`, data, options);
  }

  updateAmenity(id: any, data: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.put(`${this.apiURL}/amenities/${id}`, data, options);
  }

  destroyAmenity(id: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.delete(`${this.apiURL}/amenities/${id}`, options);
  }

  // CRUD Providers

  getProviders(data: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.get(`${this.apiURL}/suppliers?${this.parseQueryParams(data)}`, options);
  }

  getProvider(id: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.get(`${this.apiURL}/suppliers/${id}`, options);
  }

  createProvider(data: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.post(`${this.apiURL}/suppliers`, data, options);
  }

  updateProvider(id: any, data: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.put(`${this.apiURL}/suppliers/${id}`, data, options);
  }

  destroyProvider(id: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.delete(`${this.apiURL}/suppliers/${id}`, options);
  }

  updateProviderAvatar(id: any, data: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.put(`${this.apiURL}/suppliers/update_avatar/${id}`, data, options);
  }

  destroyProviderAvatar(id: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.delete(`${this.apiURL}/suppliers/delete_avatar/${id}`, options);
  }

  // CRUD Floors
  getFloors(data: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.get(`${this.apiURL}/floors?${this.parseQueryParams(data)}`, options);
  }

  getFloor(id: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.get(`${this.apiURL}/floors/${id}`, options);
  }

  createFloor(data: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.post(`${this.apiURL}/floors`, data, options)
    .pipe(
      catchError(this.errorHandler)
    )
  }

  updateFloor(id: any, data: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.put(`${this.apiURL}/floors/${id}`, data, options);
  }

  destroyFloor(id: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.delete(`${this.apiURL}/floors/${id}`, options);
  }

  // CRUD Units
  getUnits(data: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.get(`${this.apiURL}/units?${this.parseQueryParams(data)}`, options);
  }

  getUnit(id: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.get(`${this.apiURL}/units/${id}`, options)
    .pipe(
      catchError(this.errorHandler)
    )
    .pipe(map((data: any) => {
      const unitData = data.data;
      const unit: any = {
        id: unitData?.id,
        name: unitData?.attributes?.name,
        number: unitData?.attributes?.number,
        bedrooms: unitData?.attributes?.bedrooms,
        bathrooms: unitData?.attributes?.bathrooms,
        parking: unitData?.attributes?.parking,
        area: unitData?.attributes?.area,
        floor_id: unitData?.attributes?.floor_id,
        created_at: unitData?.attributes?.created_at,
        updated_at: unitData?.attributes?.updated_at,
        unit_type_id: unitData?.attributes?.unit_type_id,
        tanque_id: unitData?.attributes?.tanque_id,
        discount: unitData?.attributes?.discount,
        discount_type_id: resolveDiscountTypeId(
          unitData?.attributes?.discount_type_id ?? unitData?.attributes?.discount_type
        ),
        show_discount_on_receipt: resolveBoolean(unitData?.attributes?.show_discount_on_receipt),
        inquilinos: unitData?.attributes?.inquilinos,
        subscriptions: unitData?.attributes?.subscriptions,
        factor: unitData?.attributes?.factor,
        medidor: unitData?.attributes?.medidor,
        admin_price: unitData?.attributes?.admin_price,
        interests: unitData?.attributes?.interests,
        reconexion_fee: unitData?.attributes?.reconexion_fee,
        status: unitData?.attributes?.status,
        periodicity: unitData?.attributes?.periodicity,
        credit: unitData?.attributes?.credit,
        thsm_address_id: unitData?.attributes?.thsm_address_id,
        contact_id: unitData?.attributes?.contact_id,
        gas_meters: unitData?.attributes?.gas_meters,
        tenants: unitData?.attributes?.tenants
      }
      return unit;
    }
    ));
  }

  createUnit(data: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.post(`${this.apiURL}/units`, data, options);
  }

  updateUnit(id: any, data: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.put(`${this.apiURL}/units/${id}`, data, options);
  }

  destroyUnit(id: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.delete(`${this.apiURL}/units/${id}`, options);
  }

  // getUnitByFloor(id: any): Observable<any> {
    // return this.httpClient.get(`${this.apiURL}/units/${id}/by-floor`);
  // }

  updateUnitContrato(id: any, data: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.put(`${this.apiURL}/units/update_contrato/${id}`, data, options);
  }

  getUnitSettlement(id: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.get(`${this.apiURL}/units/settlement/${id}`, options);
  }

  // CRUD Tenants
  getTenants(data: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.get(`${this.apiURL}/tenants?${this.parseQueryParams(data)}`, options);
  }

  getTenantsQuery(data: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.get(`${this.apiURL}/tenants/query?${this.parseQueryParams(data)}`, options)
    .pipe(catchError(this.errorHandler))
    .pipe(map((tenantsData: any) => {
      const tenants: Tenant[] = [];
      tenantsData.data.forEach((tenantData: any) => {
        const tenant: Tenant = {
          id: tenantData?.id,
          name: tenantData?.attributes?.name,
          last_name: tenantData?.attributes?.last_name,
          email: tenantData?.attributes?.email,
          phone: tenantData?.attributes?.phone,
          uid: tenantData?.attributes?.uid,
          unit_id: tenantData?.attributes?.unit_id,
          created_at: tenantData?.attributes?.created_at,
          updated_at: tenantData?.attributes?.updated_at,
          encrypted_password: tenantData?.attributes?.encrypted_password,
          reset_password_token: tenantData?.attributes?.reset_password_token,
          reset_password_sent_at: tenantData?.attributes?.reset_password_sent_at,
          remember_created_at: tenantData?.attributes?.remember_created_at,
          sign_in_count: tenantData?.attributes?.sign_in_count,
          current_sign_in_at: tenantData?.attributes?.current_sign_in_at,
          last_sign_in_at: tenantData?.attributes?.last_sign_in_at,
          current_sign_in_ip: tenantData?.attributes?.current_sign_in_ip,
          last_sign_in_ip: tenantData?.attributes?.last_sign_in_ip,
          confirmation_token: tenantData?.attributes?.confirmation_token,
          confirmed_at: tenantData?.attributes?.confirmed_at,
          confirmation_sent_at: tenantData?.attributes?.confirmation_sent_at,
          unconfirmed_email: tenantData?.attributes?.unconfirmed_email,
          failed_attempts: tenantData?.attributes?.failed_attempts,
          unlock_token: tenantData?.attributes?.unlock_token,
          locked_at: tenantData?.attributes?.locked_at,
          jti: tenantData?.attributes?.jti,
          dob: tenantData?.attributes?.dob,
          usename: tenantData?.attributes?.usename,
          role_id: tenantData?.attributes?.role_id,
          profile_id: tenantData?.attributes?.profile_id,
          merchant: tenantData?.attributes?.merchant,
          customer_id: tenantData?.attributes?.customer_id,
          bank_code: tenantData?.attributes?.bank_code,
          is_primary: tenantData?.attributes?.is_primary,
          public_record_id: tenantData?.attributes?.public_record_id,
          last_name_m: tenantData?.attributes?.last_name_m,
          rfc: tenantData?.attributes?.rfc,
          thsm_id: tenantData?.attributes?.thsm_id,
          trade_id: tenantData?.attributes?.trade_id,
          account_number: tenantData?.attributes?.account_number,
          clabe_account_number: tenantData?.attributes?.clabe_account_number,
          units: tenantData?.attributes?.units,
          tenant_fiscal: tenantData?.attributes?.tenant_fiscal,
          tenant_address: tenantData?.attributes?.tenant_address
        }
        tenants.push(tenant);
      });
      const meta: any = {
        per_page: tenantsData?.meta?.params?.per_page,
        total_entries: tenantsData?.meta?.pagination ? tenantsData?.meta?.pagination?.total_entries : tenantsData.meta.meta.total_entries,
        current_page: tenantsData?.meta?.pagination ? tenantsData?.meta?.pagination?.current_page : tenantsData.meta.meta.current_page,
      };
      return { data: tenants, meta: meta };
    }));
  }

  getTenant(id: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.get(`${this.apiURL}/tenants/${id}`, options)
    .pipe(catchError(this.errorHandler))
    .pipe(map((data: any) => {
      const tenantData = data.data;
      const tenant: Tenant = {
        id: tenantData?.id,
        name: tenantData?.attributes?.name,
        last_name: tenantData?.attributes?.last_name,
        email: tenantData?.attributes?.email,
        phone: tenantData?.attributes?.phone,
        uid: tenantData?.attributes?.uid,
        unit_id: tenantData?.attributes?.unit_id,
        created_at: tenantData?.attributes?.created_at,
        updated_at: tenantData?.attributes?.updated_at,
        encrypted_password: tenantData?.attributes?.encrypted_password,
        reset_password_token: tenantData?.attributes?.reset_password_token,
        reset_password_sent_at: tenantData?.attributes?.reset_password_sent_at,
        remember_created_at: tenantData?.attributes?.remember_created_at,
        sign_in_count: tenantData?.attributes?.sign_in_count,
        current_sign_in_at: tenantData?.attributes?.current_sign_in_at,
        last_sign_in_at: tenantData?.attributes?.last_sign_in_at,
        current_sign_in_ip: tenantData?.attributes?.current_sign_in_ip,
        last_sign_in_ip: tenantData?.attributes?.last_sign_in_ip,
        confirmation_token: tenantData?.attributes?.confirmation_token,
        confirmed_at: tenantData?.attributes?.confirmed_at,
        confirmation_sent_at: tenantData?.attributes?.confirmation_sent_at,
        unconfirmed_email: tenantData?.attributes?.unconfirmed_email,
        failed_attempts: tenantData?.attributes?.failed_attempts,
        unlock_token: tenantData?.attributes?.unlock_token,
        locked_at: tenantData?.attributes?.locked_at,
        jti: tenantData?.attributes?.jti,
        dob: tenantData?.attributes?.dob,
        usename: tenantData?.attributes?.usename,
        role_id: tenantData?.attributes?.role_id,
        role: tenantData?.attributes?.role,
        profile_id: tenantData?.attributes?.profile_id,
        merchant: tenantData?.attributes?.merchant,
        customer_id: tenantData?.attributes?.customer_id,
        bank_code: tenantData?.attributes?.bank_code,
        is_primary: tenantData?.attributes?.is_primary,
        public_record_id: tenantData?.attributes?.public_record_id,
        last_name_m: tenantData?.attributes?.last_name_m,
        rfc: tenantData?.attributes?.rfc,
        thsm_id: tenantData?.attributes?.thsm_id,
        trade_id: tenantData?.attributes?.trade_id,
        account_number: tenantData?.attributes?.account_number,
        clabe_account_number: tenantData?.attributes?.clabe_account_number,
        units: tenantData?.attributes?.units,
        tenant_fiscal: tenantData?.attributes?.tenant_fiscal,
        tenant_address: tenantData?.attributes?.tenant_address
      }
      return tenant;
    }));
  }

  createTenant(data: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.post(`${this.apiURL}/tenants`, data, options);
  }

  updateTenant(id: any, data: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.put(`${this.apiURL}/tenants/${id}`, data, options);
  }

  destroyTenant(id: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.delete(`${this.apiURL}/tenants/${id}`, options);
  }

  addTenantUnit(id: any, unit_id: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.get(`${this.apiURL}/tenants/${id}/add-unit/${unit_id}`, options);
  }

  // Tenant Address

  getTenantAddress(id: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.get(`${this.apiURL}/tenant_addresses/${id}`, options);
  }

  createTenantAddress(data: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.post(`${this.apiURL}/tenant_addresses`, data, options);
  }

  updateTenantAddress(id: any, data: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.put(`${this.apiURL}/tenant_addresses/${id}`, data, options);
  }

  destroyTenantAddress(id: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.delete(`${this.apiURL}/tenant_addresses/${id}`, options);
  }

  // Tentant Fiscal

  getTenantFiscal(id: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.get(`${this.apiURL}/tenant_fiscals/${id}`, options);
  }

  createTenantFiscal(data: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.post(`${this.apiURL}/tenant_fiscals`, data, options);
  }

  updateTenantFiscal(id: any, data: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.put(`${this.apiURL}/tenant_fiscals/${id}`, data, options);
  }

  destroyTenantFiscal(id: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.delete(`${this.apiURL}/tenant_fiscals/${id}`, options)
    .pipe(catchError(this.errorHandler));
  }

  // CRUD Payment methods
  getPaymentMethods(): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.get(`${this.apiURL}/payment_methods`, options);
  }

  getPaymentMethodsByTenant(data: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.get(`${this.apiURL}/payment_methods/by-tenant/${data}`, options);
  }

  // CRUD Utilities
  getUtilities(data: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.get(`${this.apiURL}/utilities?${this.parseQueryParams(data)}`, options)
    .pipe(
      catchError(this.errorHandler)
    )
    .pipe(map((utilitiesData: any) => {
      const utilities: any = [];
      utilitiesData.data.forEach((utility_serialized: any) => {
        const utility: Utility = {
          id: utility_serialized.id,
          name: utility_serialized.name,
          description: utility_serialized.description,
          status: utility_serialized.status,
          utility_setting: utility_serialized.utility_setting,
          zones: utility_serialized.zones,
        }
        utilities.push(utility);
      });
      return { data: utilities, meta: utilitiesData.meta };
    }));
  }

  getUtility(id: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.get(`${this.apiURL}/utilities/${id}`, options)
    .pipe(
      catchError(this.errorHandler)
    )
    .pipe(map((data: any) => {
      const utilityData = data.data;
      const utility: Utility = {
        id: utilityData?.id,
        name: utilityData?.attributes?.name,
        description: utilityData?.attributes?.description,
        status: utilityData?.attributes?.status,
        utility_setting: utilityData?.attributes?.utility_setting,
        zones: utilityData?.attributes?.zones
      }
      return utility;
    } ));
  }

  createUtility(data: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.post(`${this.apiURL}/utilities`, data, options);
  }

  updateUtility(id: any, data: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.put(`${this.apiURL}/utilities/${id}`, data, options);
  }

  destroyUtility(id: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.delete(`${this.apiURL}/utilities/${id}`, options);
  }

  // CRUD Utility Settings
  getUtilitySettings(data: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.get(`${this.apiURL}/utility_settings?${this.parseQueryParams(data)}`, options);
  }

  getZonesByUtility(data: any): Promise<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.get(`${this.apiURL}/utility_settings/zone_list/${data}`, options).toPromise();
  }

  createUtilitySetting(data: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.post(`${this.apiURL}/utility_settings`, data, options);
  }

  getUtilityPrice(data: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.post(`${this.apiURL}/utility_settings/last`, data, options);
  }

  // CRUD Tanques

  getAgreements(initialPercentage?: number): Observable<InventoryAgreement[]> {
    const options = this.getHeaders(this.token);
    const query = initialPercentage === undefined
      ? ''
      : `?initial_percentage=${encodeURIComponent(initialPercentage)}`;
    return this.httpClient.get<InventoryAgreement[]>(`${this.apiURL}/inventory_agreements${query}`, options)
  }

  getTankPricePreview(buildingId: number | string): Observable<TankPricePreview> {
    const options = this.getHeaders(this.token);
    const url = `${this.apiURL}/tanques?virtual=price_preview&building_id=${encodeURIComponent(buildingId)}`;
    return this.httpClient.get<any>(url, options).pipe(
      map((response: any) => {
        const resource = Array.isArray(response)
          ? response[0]
          : Array.isArray(response?.data)
            ? response.data[0]
            : response?.data?.attributes ?? response?.data ?? response;
        const attributes = resource?.attributes ?? resource ?? {};
        return {
          region_price: this.toNullableNumber(attributes.region_price),
          sale_price: this.toNullableNumber(attributes.sale_price),
          cost: this.toNullableNumber(attributes.cost)
        };
      })
    );
  }

  getTanques(data: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.get(`${this.apiURL}/tanques/query?${this.parseQueryParams(data)}`, options)
    .pipe(
      catchError(this.errorHandler)
    )
    .pipe(map((tanquesData: any) => {
      const tanques: Tanque[] = [];
      tanquesData.data.forEach((tanqueData: any) => {
        const tanque: Tanque = {
          id: tanqueData?.id,
          name: tanqueData?.name,
          capacidad: tanqueData?.capacidad,
          unidad: tanqueData?.unidad,
          limite: tanqueData?.limite,
          created_at: tanqueData?.created_at,
          updated_at: tanqueData?.updated_at,
          building_id: tanqueData?.building_id,
          limite_inferior: tanqueData?.limite_inferior,
          optimal: tanqueData?.optimal,
          folio_qr: tanqueData?.folio_qr,
          latitude: tanqueData?.latitude,
          longitude: tanqueData?.longitude,
          parent_tanque_id: tanqueData?.parent_tanque_id,
          external_id: tanqueData?.external_id,
          building: tanqueData?.building,
          settlement: tanqueData?.settlement,
          initial_percentage: tanqueData.initial_percentage,
          final_percentage: tanqueData.final_percentage,
          trajectory_distance: tanqueData?.trajectory_distance,
          trajectory_video_url: tanqueData?.trajectory_video_url
        }
        tanques.push(tanque);
      });
      const meta: any = {
        per_page: tanquesData?.meta?.params?.per_page,
        total_entries: tanquesData?.meta?.pagination?.total_entries,
        current_page: tanquesData?.meta?.pagination?.current_page,
      };
      return { data: tanques, meta: meta };
    }))
  }

  deleteVideo(id: number | string) {
    const options = this.getHeaders(this.token);
    return this.httpClient.delete<any>(`${this.apiURL}/tanques/${id}/trajectory`);
  }

  getTanque(id: any, virtual = false): Observable<any> {
    const options = this.getHeaders(this.token);
    const url = virtual ? `${this.apiURL}/tanques/${id}?virtual=VIDEO` : `${this.apiURL}/tanques/${id}`
    return this.httpClient.get(url, options)
    .pipe(
      catchError(this.errorHandler)
    )
    .pipe(map((tanquesData: any) => {
      const tanqueData = virtual
        ? {
          attributes: tanquesData
        }
        : tanquesData.data
      const tanque: Tanque = {
        id: virtual ? tanqueData.attributes.id : tanqueData?.id,
        name: tanqueData?.attributes?.name,
        capacidad: tanqueData?.attributes?.capacidad,
        unidad: tanqueData?.attributes?.unidad,
        limite: tanqueData?.attributes?.limite,
        created_at: tanqueData?.attributes?.created_at,
        updated_at: tanqueData?.attributes?.updated_at,
        building_id: tanqueData?.attributes?.building_id,
        limite_inferior: tanqueData?.attributes?.limite_inferior,
        inventory_agreement_id: tanqueData?.attributes?.inventory_agreement_id,
        optimal: tanqueData?.attributes?.optimal,
        folio_qr: tanqueData?.attributes?.folio_qr,
        latitude: tanqueData?.attributes?.latitude,
        longitude: tanqueData?.attributes?.longitude,
        parent_tanque_id: tanqueData?.attributes?.parent_tanque_id,
        external_id: tanqueData?.attributes?.external_id,
        building: tanqueData?.attributes?.building,
        settlement: tanqueData?.attributes?.settlement,
        initial_percentage: tanqueData?.attributes.initial_percentage,
        final_percentage: tanqueData?.attributes.final_percentage,
        trajectory_distance: tanqueData?.attributes.trajectory_distance,
        trajectory_video_url: tanqueData?.attributes.trajectory_video_url,
        region_price: tanqueData?.attributes.region_price,
        sale_price: tanqueData?.attributes.sale_price,
        cost: tanqueData?.attributes.cost,
        inventory_replacement_cost: tanqueData?.attributes.inventory_replacement_cost,
        debited_amount: tanqueData?.attributes.debited_amount,
        external_gas_company_agreed_price: tanqueData?.attributes.external_gas_company_agreed_price,
        inventory_payment_liters: tanqueData?.attributes.inventory_payment_liters,
        inventory_payment_total: tanqueData?.attributes.inventory_payment_total,
        inventory_payment_pdf_url: tanqueData?.attributes.inventory_payment_pdf_url,
        inventory_payment_xml_url: tanqueData?.attributes.inventory_payment_xml_url
      }
      return tanque;
    }
    ));
  }

  getTanqueByBuilding(id: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.get(`${this.apiURL}/tanques/building/${id}`, options);
  }

  createTanque(data: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.post(`${this.apiURL}/tanques`, data, options);
  }

  otpCodeApprovalTank(token: string, idTank: number) {
    const options = this.getHeaders(token);
    return this.httpClient.post(`${this.apiURL}/tanques/${idTank}/approval_otp`,null, options);
  }

  otpCodeGasMeter(token: string, meterId: number) {
    const options = this.getHeaders(token);
    return this.httpClient.post(`${this.apiURL}/gas_meters/${meterId}/approval_otp`,null, options);
  }

  createApproval(token: string, id: number, payload: any, type = 'tank') {
    const url = type === 'gas_meter'
      ? `${this.apiURL}/gas_meters/${id}/gas_meter_approvals`
      : `${this.apiURL}/tanques/${id}/tank_approvals`;
    const options = this.getHeaders(token);
    return this.http.post(url, payload, options);
  }

  saveTankPlans(idTank: string, data: FormData): Observable<any>{
    const options = this.getHeadersMultipart(this.token);
    return this.httpClient.post(`${this.apiURL}/tanques/${idTank}/blueprints`, data, options);
  }

  removeTankPlan(idTank: number, idFile: number) {
    const options = this.getHeaders(this.token);
    return this.httpClient.delete(`${this.apiURL}/tanques/${idTank}/blueprints/${idFile}`, options);
  }

  getTankPlans(idTank: number): Observable<any>{
    const options = this.getHeaders(this.token);
    return this.httpClient.get(`${this.apiURL}/tanques/${idTank}/blueprints`, options);
  }

  getBlobFile(url: string, isPromise: boolean) {
    const options = this.getHeaders(this.token);
    const observable = this.httpClient.get(`${url}`, {...options, responseType: 'blob'});
    return isPromise ? firstValueFrom(observable) : observable;
  }

  updateTanque(id: any, data: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.put(`${this.apiURL}/tanques/${id}`, data, options);
  }

  updateUnitInventoryCreditConfiguration(
    unitId: number | string,
    configuration: InventoryCreditConfiguration
  ): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.put(
      `${this.apiURL}/units/${unitId}?virtual=INVENTORY_CREDIT_CONFIGURATION`,
      configuration,
      options
    );
  }

  uploadTankInventoryPaymentFile(
    tankId: number | string,
    type: 'pdf' | 'xml',
    file: File
  ): Observable<any> {
    const options = this.getHeadersMultipart(this.token);
    const formData = new FormData();
    formData.append('file', file);
    return this.httpClient.put(
      `${this.apiURL}/tanques/${tankId}/inventory_payment_${type}`,
      formData,
      options
    );
  }

  private toNullableNumber(value: unknown): number | null {
    if (value === null || value === undefined || value === '') {
      return null;
    }
    const number = Number(value);
    return Number.isFinite(number) ? number : null;
  }

  destroyTanque(id: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.delete(`${this.apiURL}/tanques/${id}`, options);
  }

  getTanquesBySettlement(id: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.get(`${this.apiURL}/tanques/${id}/settlement`, options);
  }

  getTanquesByBuilding(id: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.get(`${this.apiURL}/tanques/${id}/building`, options);
  }

  getTanqueReport(data: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.get(`${this.apiURL}/tanques/report?${this.parseQueryParams(data)}`, options);
  }

  getTanqueEstimation(id: any, data: any): Observable<any> {
    const options = this.getHeaders(this.token);
    // return this.httpClient.get(`http://localhost:3000/api/v1/tanques/estimation/${id}`);
    return this.httpClient.get(`${this.apiURL}/tanques/estimation/${id}?${this.parseQueryParams(data)}`, options);
  }

  getTanqueAnova(id: any, data: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.get(`${this.apiURL}/tanques/anova/${id}?${this.parseQueryParams(data)}`, options);
  }

  getTanqueRiReport(): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.get(`${this.apiURL}/tanques/ri_report`, options);
  }

  getChildrenTanks(id: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.get(`${this.apiURL}/tanques/children/${id}`, options);
  }

  // CRUD Payment Concepts

  getPaymentConcepts(data: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.get(`${this.apiURL}/payment_concepts?${this.parseQueryParams(data)}`, options);
  }

  getPaymentConcept(id: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.get(`${this.apiURL}/payment_concepts/${id}`, options);
  }

  createPaymentConcept(data: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.post(`${this.apiURL}/payment_concepts`, data, options);
  }

  updatePaymentConcept(id: any, data: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.put(`${this.apiURL}/payment_concepts/${id}`, data, options);
  }

  destroyPaymentConcept(id: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.delete(`${this.apiURL}/payment_concepts/${id}`, options);
  }

  // QUERIES
  getBuildingsBySettlement(settlementId: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.get(`${this.apiURL}/settlements/${settlementId}/buildings`, options)
    .pipe(catchError(this.errorHandler))
    .pipe(map((buildingsData: any) => {
      const buildings: any = [];
      buildingsData.data.forEach((buildingData: any) => {
        const building: any = {
          id: buildingData?.id,
          name: buildingData?.attributes?.name,
          clave: buildingData?.attributes?.clave,
          settlement: buildingData?.attributes?.settlement,
          floors: buildingData?.attributes?.floors,
          utilities: buildingData?.attributes?.utilities,
          tanques: buildingData?.attributes?.tanques,
          updated_at: buildingData?.attributes?.updated_at,
          created_at: buildingData?.attributes?.created_at,
        }
        buildings.push(building);
      });
      return buildings
    }));
  }

  getFloorsByBuilding(buildingId: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.get(`${this.apiURL}/buildings/${buildingId}/floors`, options);
  }

  // getUnitsByFloor(floorId: any): Observable<any> {
    // const options = this.getHeaders(this.token);
    // return this.httpClient.get(`${this.apiURL}/floors/${floorId}/units`, options);
  // }

  // getAmenityBySettlement(settlementId: any): Observable<any> {
    // const options = this.getHeaders(this.token);
    // return this.httpClient.get(`${this.apiURL}/settlements/${settlementId}/amenities`, options);
  // }

  // getServiceBySettlement(settlementId: any): Observable<any> {
    // const options = this.getHeaders(this.token);
    // return this.httpClient.get(`${this.apiURL}/settlements/${settlementId}/services`, options);
  // }

  getUtilityBySettlement(settlementId: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.get(`${this.apiURL}/settlements/${settlementId}/utilities`, options);
  }

  getBill(id: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.get(`${this.apiURL}/bills/${id}?all=true`, options)
    .pipe(catchError(this.errorHandler))
    .pipe(map((billData: any) => {

      const billItems: BillItem[] = [];
      billData?.included.map((billItemData: any) => {
        const billItem: BillItem = {
          id: billItemData?.id,
          bill_id: billItemData?.attributes?.bill_id, 
          item_type: billItemData?.attributes?.item_type, 
          item_id: billItemData?.attributes?.item_id,
          quantity: billItemData?.attributes?.quantity,
          price: billItemData?.attributes?.price,
          total: billItemData?.attributes?.total,
          sku: billItemData?.attributes?.sku,
          discount_amount: billItemData?.attributes?.discount_amount,
          net_price: billItemData?.attributes?.net_price,
          tax_rate: billItemData?.attributes?.tax_rate,
          tax_amount: billItemData?.attributes?.tax_amount,
          gas_meter_read: {
            id: billItemData?.attributes?.gas_meter_read?.data?.id,
            value: billItemData?.attributes?.gas_meter_read?.data?.attributes?.value,
            unit_id: billItemData?.attributes?.gas_meter_read?.data?.attributes?.unit_id,
            ocr_value: billItemData?.attributes?.gas_meter_read?.data?.attributes?.ocr_value,
            value_prev: billItemData?.attributes?.gas_meter_read?.data?.attributes?.value_prev,
            status: billItemData?.attributes?.gas_meter_read?.data?.attributes?.status,
            motive: billItemData?.attributes?.gas_meter_read?.data?.attributes?.motive,
            gas_meter_id: billItemData?.attributes?.gas_meter_read?.data?.attributes?.gas_meter_id,
            consumo_m3: billItemData?.attributes?.gas_meter_read?.data?.attributes?.consumo_m3,
            consumo_lt: billItemData?.attributes?.gas_meter_read?.data?.attributes?.consumo_lt,
            consumo_periodo: billItemData?.attributes?.gas_meter_read?.data?.attributes?.consumo_periodo,
            discount: billItemData?.attributes?.gas_meter_read?.data?.attributes?.discount,
            consumo_total: billItemData?.attributes?.gas_meter_read?.data?.attributes?.consumo_total,
            gas_price: billItemData?.attributes?.gas_meter_read?.data?.attributes?.gas_price,
            bill_item_id: billItemData?.attributes?.gas_meter_read?.data?.attributes?.bill_item_id,
            photo: billItemData?.attributes?.gas_meter_read?.data?.attributes?.photo
          },
          cancelled: billItemData?.attributes?.cancelled,
        }
        billItems.push(billItem);
      });

      const bill: Bill = {
        id: billData?.data?.id,
        reference: billData?.data?.attributes?.reference,
        description: billData?.data?.attributes?.description,
        status: billData?.data?.attributes?.status,
        consumo_m3: billData?.data?.attributes?.consumo_m3,
        consumo_lt: billData?.data?.attributes?.consumo_lt,
        consumo_periodo: billData?.data?.attributes?.consumo_periodo,
        sub_total: billData?.data?.attributes?.sub_total,
        total: billData?.data?.attributes?.total,
        days: billData?.data?.attributes?.days,
        read_prev: billData?.data?.attributes?.read_prev,
        read_curr: billData?.data?.attributes?.read_curr,
        discount: billData?.data?.attributes?.discount,
        periodo_inicio: billData?.data?.attributes?.periodo_inicio,
        periodo_fin: billData?.data?.attributes?.periodo_fin,
        due_date: billData?.data?.attributes?.due_date,
        paid_date: billData?.data?.attributes?.paid_date,
        created_at: billData?.data?.attributes?.created_at,
        updated_at: billData?.data?.attributes?.updated_at,
        subscription_id: billData?.data?.attributes?.subscription_id,
        admin_price: billData?.data?.attributes?.admin_price,
        interests: billData?.data?.attributes?.interests,
        taxes: billData?.data?.attributes?.taxes,
        service_sub_total: billData?.data?.attributes?.service_sub_total,
        additional_services: billData?.data?.attributes?.additional_services,
        unit_id: billData?.data?.attributes?.unit_id,
        recargos: billData?.data?.attributes?.recargos,
        consumo_total: billData?.data?.attributes?.consumo_total,
        state: billData?.data?.attributes?.state,
        pdf: billData?.data?.attributes?.pdf,
        invoiced: billData?.data?.attributes?.invoiced,
        invoice_id: billData?.data?.attributes?.invoice_id,
        adv: billData?.data?.attributes?.adv,
        retry: billData?.data?.attributes?.retry,
        gas_price: billData?.data?.attributes?.gas_price,
        cancel_interests: billData?.data?.attributes?.cancel_interests,
        cancel_recargos: billData?.data?.attributes?.cancel_recargos,
        merchant_id: billData?.data?.attributes?.merchant_id,
        sales_order: billData?.data?.attributes?.sales_order,
        settlement: billData?.data?.attributes?.settlement,
        unit: billData?.data?.attributes?.unit,
        tenant: billData?.data?.attributes?.tenant,
        bill_items: billItems
      }
      return bill;
    }));
  }

  getBillAuditTrail(id: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.get(`${this.apiURL}/bills/${id}/audit_trail`, options)
    .pipe(catchError(this.errorHandler));
  }

  getSettlementAuditTrail(id: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.get(`${this.apiURL}/settlements/${id}/audit_trail`, options)
    .pipe(catchError(this.errorHandler));
  }

  getGasMeterAuditTrail(id: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.get(`${this.apiURL}/gas_meters/${id}/audit_trail`, options)
    .pipe(catchError(this.errorHandler));
  }

  getBillsQuery(data: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.get(`${this.apiURL}/bills?${this.parseQueryParams(data)}`, options)
    .pipe(catchError(this.errorHandler))
    .pipe(map((billsData: any) => {
      const bills: any = [];
      billsData.data.forEach((billData: any) => {
        const bill: any = {
          id: billData?.id,
          reference: billData?.attributes?.reference,
          description: billData?.attributes?.description,
          status: billData?.attributes?.status,
          consumo_m3: billData?.attributes?.consumo_m3,
          consumo_lt: billData?.attributes?.consumo_lt,
          consumo_periodo: billData?.attributes?.consumo_periodo,
          sub_total: billData?.attributes?.sub_total,
          total: billData?.attributes?.total,
          days: billData?.attributes?.days,
          read_prev: billData?.attributes?.read_prev,
          read_curr: billData?.attributes?.read_curr,
          discount: billData?.attributes?.discount,
          periodo_inicio: billData?.attributes?.periodo_inicio,
          periodo_fin: billData?.attributes?.periodo_fin,
          due_date: billData?.attributes?.due_date,
          paid_date: billData?.attributes?.paid_date,
          created_at: billData?.attributes?.created_at,
          updated_at: billData?.attributes?.updated_at,
          subscription_id: billData?.attributes?.subscription_id,
          admin_price: billData?.attributes?.admin_price,
          interests: billData?.attributes?.interests,
          taxes: billData?.attributes?.taxes,
          service_sub_total: billData?.attributes?.service_sub_total,
          additional_services: billData?.attributes?.additional_services,
          unit_id: billData?.attributes?.unit_id,
          recargos: billData?.attributes?.recargos,
          consumo_total: billData?.attributes?.consumo_total,
          state: billData?.attributes?.state,
          pdf: billData?.attributes?.pdf,
          invoiced: billData?.attributes?.invoiced,
          invoice_id: billData?.attributes?.invoice_id,
          adv: billData?.attributes?.adv,
          retry: billData?.attributes?.retry,
          gas_price: billData?.attributes?.gas_price,
          cancel_interests: billData?.attributes?.cancel_interests,
          cancel_recargos: billData?.attributes?.cancel_recargos,
          merchant_id: billData?.attributes?.merchant_id,
          sales_order: billData?.attributes?.sales_order,
          settlement: billData?.attributes?.settlement,
          unit: billData?.attributes?.unit,
          tenant: billData?.attributes?.tenant,
        }
        bills.push(bill);
      });
      const meta: any = {
        per_page: billsData?.meta?.params?.per_page,
        total_entries: billsData?.meta?.pagination?.total_entries,
        current_page: billsData?.meta?.pagination?.current_page,
      };
      return { data: bills, meta: meta };
    }
    ));
  }
  
  // getBillStatusByUnit(data: any): Observable<any> {
    // const options = this.getHeaders(this.token);
    // return this.httpClient.post(`${this.apiURL}/bills/status/by-unit`, data, options);
  // }

  getBillsBySettlement(id: any, data: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.get(`${this.apiURL}/bills/${id}/settlement?${this.parseQueryParams(data)}`, options);
  }

  getBillsByUnit(id: any, data: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.get(`${this.apiURL}/bills/${id}/unit?${this.parseQueryParams(data)}`, options)
    .pipe(catchError(this.errorHandler))
    .pipe(map((billsData: any) => {
      const bills: any = [];
      billsData.data.forEach((billData: any) => {
        const bill: any = {
          id: billData?.id,
          reference: billData?.attributes?.reference,
          description: billData?.attributes?.description,
          status: billData?.attributes?.status,
          consumo_m3: billData?.attributes?.consumo_m3,
          consumo_lt: billData?.attributes?.consumo_lt,
          consumo_periodo: billData?.attributes?.consumo_periodo,
          sub_total: billData?.attributes?.sub_total,
          total: billData?.attributes?.total,
          days: billData?.attributes?.days,
          read_prev: billData?.attributes?.read_prev,
          read_curr: billData?.attributes?.read_curr,
          discount: billData?.attributes?.discount,
          periodo_inicio: billData?.attributes?.periodo_inicio,
          periodo_fin: billData?.attributes?.periodo_fin,
          due_date: billData?.attributes?.due_date,
          paid_date: billData?.attributes?.paid_date,
          created_at: billData?.attributes?.created_at,
          updated_at: billData?.attributes?.updated_at,
          subscription_id: billData?.attributes?.subscription_id,
          admin_price: billData?.attributes?.admin_price,
          interests: billData?.attributes?.interests,
          taxes: billData?.attributes?.taxes,
          service_sub_total: billData?.attributes?.service_sub_total,
          additional_services: billData?.attributes?.additional_services,
          unit_id: billData?.attributes?.unit_id,
          recargos: billData?.attributes?.recargos,
          consumo_total: billData?.attributes?.consumo_total,
          state: billData?.attributes?.state,
          pdf: billData?.attributes?.pdf,
          invoiced: billData?.attributes?.invoiced,
          invoice_id: billData?.attributes?.invoice_id,
          adv: billData?.attributes?.adv,
          retry: billData?.attributes?.retry,
          gas_price: billData?.attributes?.gas_price,
          cancel_interests: billData?.attributes?.cancel_interests,
          cancel_recargos: billData?.attributes?.cancel_recargos,
          merchant_id: billData?.attributes?.merchant_id,
          sales_order: billData?.attributes?.sales_order,
          settlement: billData?.attributes?.settlement,
          unit: billData?.attributes?.unit,
          tenant: billData?.attributes?.tenant,
        }
        bills.push(bill);
      });
      const meta: any = {
        per_page: billsData?.meta?.params?.per_page,
        total_entries: billsData?.meta?.pagination?.total_entries,
        current_page: billsData?.meta?.pagination?.current_page,
      };
      return { data: bills, meta: meta };
    }
    ));
  }

  getBillsByUnits(id: any, data: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.get(`${this.apiURL}/bills/unit/${id}?${this.parseQueryParams(data)}`, options)
    .pipe(catchError(this.errorHandler))
    .pipe(map((billData: any) => {
      let bills: Bill[] = [];
      billData?.data.map((billData: any) => {
        const bill: any = {
          id: billData?.id,
          reference: billData?.attributes?.reference,
          description: billData?.attributes?.description,
          status: billData?.attributes?.status,
          consumo_m3: billData?.attributes?.consumo_m3,
          consumo_lt: billData?.attributes?.consumo_lt,
          consumo_periodo: billData?.attributes?.consumo_periodo,
          sub_total: billData?.attributes?.sub_total,
          total: billData?.attributes?.total,
          days: billData?.attributes?.days,
          read_prev: billData?.attributes?.read_prev,
          read_curr: billData?.attributes?.read_curr,
          discount: billData?.attributes?.discount,
          periodo_inicio: billData?.attributes?.periodo_inicio,
          periodo_fin: billData?.attributes?.periodo_fin,
          due_date: billData?.attributes?.due_date,
          paid_date: billData?.attributes?.paid_date,
          created_at: billData?.attributes?.created_at,
          updated_at: billData?.attributes?.updated_at,
          subscription_id: billData?.attributes?.subscription_id,
          admin_price: billData?.attributes?.admin_price,
          interests: billData?.attributes?.interests,
          taxes: billData?.attributes?.taxes,
          service_sub_total: billData?.attributes?.service_sub_total,
          additional_services: billData?.attributes?.additional_services,
          unit_id: billData?.attributes?.unit_id,
          recargos: billData?.attributes?.recargos,
          consumo_total: billData?.attributes?.consumo_total,
          state: billData?.attributes?.state,
          pdf: billData?.attributes?.pdf,
          invoiced: billData?.attributes?.invoiced,
          invoice_id: billData?.attributes?.invoice_id,
          adv: billData?.attributes?.adv,
          retry: billData?.attributes?.retry,
          gas_price: billData?.attributes?.gas_price,
          cancel_interests: billData?.attributes?.cancel_interests,
          cancel_recargos: billData?.attributes?.cancel_recargos,
          merchant_id: billData?.attributes?.merchant_id,
          sales_order: billData?.attributes?.sales_order,
          settlement: billData?.attributes?.settlement,
          unit: billData?.attributes?.unit,
          tenant: billData?.attributes?.tenant,
          // bill_items: billItems
        }
        bills.push(bill);
      });
      
      const meta: any = {
        per_page: billData?.meta?.params?.per_page,
        total_entries: billData?.meta?.pagination?.total_entries,
        current_page: billData?.meta?.pagination?.current_page,
      };
      return { data: bills, meta: meta };
    }));
  }

  getBillByUnitSummary(unitId: any, data: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.get(`${this.apiURL}/bills/unit/${unitId}/summary?${this.parseQueryParams(data)}`, options)
    .pipe(catchError(this.errorHandler));
  }

  getBillCalculation(data: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.post(`${this.apiURL}/bills/calculation`, data, options)
    .pipe(catchError(this.errorHandler))
    .pipe(map((billData: any) => {

      const billItems: BillItem[] = [];
      billData?.included.map((billItemData: any) => {
        const billItem: BillItem = {
          id: billItemData?.id,
          bill_id: billItemData?.attributes?.bill_id, 
          item_type: billItemData?.attributes?.item_type, 
          item_id: billItemData?.attributes?.item_id,
          quantity: billItemData?.attributes?.quantity,
          price: billItemData?.attributes?.price,
          total: billItemData?.attributes?.total,
          sku: billItemData?.attributes?.sku,
          discount_amount: billItemData?.attributes?.discount_amount,
          net_price: billItemData?.attributes?.net_price,
          tax_rate: billItemData?.attributes?.tax_rate,
          tax_amount: billItemData?.attributes?.tax_amount,
          gas_meter_read: {
            id: billItemData?.attributes?.gas_meter_read?.data?.id,
            value: billItemData?.attributes?.gas_meter_read?.data?.attributes?.value,
            unit_id: billItemData?.attributes?.gas_meter_read?.data?.attributes?.unit_id,
            ocr_value: billItemData?.attributes?.gas_meter_read?.data?.attributes?.ocr_value,
            value_prev: billItemData?.attributes?.gas_meter_read?.data?.attributes?.value_prev,
            status: billItemData?.attributes?.gas_meter_read?.data?.attributes?.status,
            motive: billItemData?.attributes?.gas_meter_read?.data?.attributes?.motive,
            gas_meter_id: billItemData?.attributes?.gas_meter_read?.data?.attributes?.gas_meter_id,
            consumo_m3: billItemData?.attributes?.gas_meter_read?.data?.attributes?.consumo_m3,
            consumo_lt: billItemData?.attributes?.gas_meter_read?.data?.attributes?.consumo_lt,
            consumo_periodo: billItemData?.attributes?.gas_meter_read?.data?.attributes?.consumo_periodo,
            discount: billItemData?.attributes?.gas_meter_read?.data?.attributes?.discount,
            consumo_total: billItemData?.attributes?.gas_meter_read?.data?.attributes?.consumo_total,
            gas_price: billItemData?.attributes?.gas_meter_read?.data?.attributes?.gas_price,
            bill_item_id: billItemData?.attributes?.gas_meter_read?.data?.attributes?.bill_item_id,
            photo: billItemData?.attributes?.gas_meter_read?.data?.attributes?.photo,
          },
          cancelled: billItemData?.attributes?.cancelled,
        }
        billItems.push(billItem);
      });

      const bill: Bill = {
        id: billData?.data?.id,
        reference: billData?.data?.attributes?.reference,
        description: billData?.data?.attributes?.description,
        status: billData?.data?.attributes?.status,
        consumo_m3: billData?.data?.attributes?.consumo_m3,
        consumo_lt: billData?.data?.attributes?.consumo_lt,
        consumo_periodo: billData?.data?.attributes?.consumo_periodo,
        sub_total: billData?.data?.attributes?.sub_total,
        total: billData?.data?.attributes?.total,
        days: billData?.data?.attributes?.days,
        read_prev: billData?.data?.attributes?.read_prev,
        read_curr: billData?.data?.attributes?.read_curr,
        discount: billData?.data?.attributes?.discount,
        periodo_inicio: billData?.data?.attributes?.periodo_inicio,
        periodo_fin: billData?.data?.attributes?.periodo_fin,
        due_date: billData?.data?.attributes?.due_date,
        paid_date: billData?.data?.attributes?.paid_date,
        created_at: billData?.data?.attributes?.created_at,
        updated_at: billData?.data?.attributes?.updated_at,
        subscription_id: billData?.data?.attributes?.subscription_id,
        admin_price: billData?.data?.attributes?.admin_price,
        interests: billData?.data?.attributes?.interests,
        taxes: billData?.data?.attributes?.taxes,
        service_sub_total: billData?.data?.attributes?.service_sub_total,
        additional_services: billData?.data?.attributes?.additional_services,
        unit_id: billData?.data?.attributes?.unit_id,
        recargos: billData?.data?.attributes?.recargos,
        consumo_total: billData?.data?.attributes?.consumo_total,
        state: billData?.data?.attributes?.state,
        pdf: billData?.data?.attributes?.pdf,
        invoiced: billData?.data?.attributes?.invoiced,
        invoice_id: billData?.data?.attributes?.invoice_id,
        adv: billData?.data?.attributes?.adv,
        retry: billData?.data?.attributes?.retry,
        gas_price: billData?.data?.attributes?.gas_price,
        cancel_interests: billData?.data?.attributes?.cancel_interests,
        cancel_recargos: billData?.data?.attributes?.cancel_recargos,
        merchant_id: billData?.data?.attributes?.merchant_id,
        sales_order: billData?.data?.attributes?.sales_order,
        settlement: billData?.data?.attributes?.settlement,
        unit: billData?.data?.attributes?.unit,
        tenant: billData?.data?.attributes?.tenant,
        bill_items: billItems
      }
      return bill;
    }));
  }

  getBillCalculationLts(data: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.post(`${this.apiURL}/bills/calculation-lts`, data, options);
  }

  getBillLatest(id: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.get(`${this.apiURL}/bills/latest/${id}`, options);
  }

  getBillsPortafolio(data: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.get(`${this.apiURL}/bills/portafolio?${this.parseQueryParams(data)}`, options);
  }

  getBillsPortafolioSettlement(data: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.post(`${this.apiURL}/bills/portafolio/settlement`, data, options);
  }

  getBillsPortafolioProfile(data: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.post(`${this.apiURL}/bills/portafolio/profile`, data, options);
  }

  createBillCustom(data: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.post(`${this.apiURL}/bills`, data, options);
  }

  // BILL ITEMS

  addBillItem(data: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.post(`${this.apiURL}/bill_items`, data, options);
  }

  deleteBillItem(id: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.delete(`${this.apiURL}/bill_items/${id}`, options)
    .pipe(catchError(this.errorHandler))
    .pipe(map((response: any) => {
      return response;
    }));
  }

  updateBillItem(id: string, data: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.put(`${this.apiURL}/bill_items/${id}`, data, options)
    .pipe(catchError(this.errorHandler))
    .pipe(map((response: any) => {
      return response;
    }));
  }


  // CONSUMPTIONS

  getConsumptionsByTanque(tanqueId: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.get(`${this.apiURL}/consumptions/tanque/${tanqueId}`, options);
  }

  getConsumptionsByProfile(data: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.get(`${this.apiURL}/consumptions/profile?${this.parseQueryParams(data)}`, options);
  }

  getConsumptionsBySettlement(data: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.get(`${this.apiURL}/consumptions/settlement?${this.parseQueryParams(data)}`, options);
  }

  getTenantByUnit(unitId: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.get(`${this.apiURL}/units/${unitId}/tenants`, options);
  }

  getUnitByTanque(tanqueId: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.get(`${this.apiURL}/tanques/units/${tanqueId}`, options);
  }

  // CRUD Task Categories

  getTaskCategories(data: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.get(`${this.apiURL}/task_categories?${this.parseQueryParams(data)}`, options);
  }

  getTaskCategory(id: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.get(`${this.apiURL}/task_categories/${id}`, options);
  }

  createTaskCategory(data: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.post(`${this.apiURL}/task_categories`, data, options);
  }

  updateTaskCategory(id: any, data: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.put(`${this.apiURL}/task_categories/${id}`, data, options);
  }

  destroyTaskCategory(id: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.delete(`${this.apiURL}/task_categories/${id}`, options);
  }

  // CRUD Tasks
  // ! TODO: Deprecated

  getTasks(data: any): Observable<any> {
    return this.httpClient.get(`${this.apiURL}/tasks?${this.parseQueryParams(data)}`);
  }

  getTask(id: any): Observable<any> {
    return this.httpClient.get(`${this.apiURL}/tasks/${id}`);
  }

  createTask(data: any): Observable<any> {
    return this.httpClient.post(`${this.apiURL}/tasks`, data);
  }

  updateTask(id: any, data: any): Observable<any> {
    return this.httpClient.put(`${this.apiURL}/tasks/${id}`, data);
  }

  destroyTask(id: any): Observable<any> {
    return this.httpClient.delete(`${this.apiURL}/tasks/${id}`);
  }

  // CRUD Schedules

  getSchedules(data: any): Observable<any> {
    return this.httpClient.get(`${this.apiURL}/schedules?${this.parseQueryParams(data)}`);
  }

  // CRUD Users
  getUsers(data: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.get(`${this.apiURL}/users?${this.parseQueryParams(data)}`, options);
  }

  getUser(id: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.get(`${this.apiURL}/users/${id}`, options)
    .pipe(catchError(this.errorHandler))
    .pipe(map((userData: any) => {
      const user: User = {
        id: parseInt(userData?.data?.id),
        email: userData?.data?.attributes,
        name: userData?.data?.attributes?.name,
        last_name: userData?.data?.attributes?.last_name,
        jti: userData?.data?.attributes?.jti,
        profile_id: userData?.data?.attributes?.profile_id,
        role: userData?.data?.attributes?.role, 
      }
      return user;
    }));
  }

  createUser(data: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.post(`${this.apiURL}/users`, data, options);
  }

  updateUser(id: any, data: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.put(`${this.apiURL}/users/${id}`, data, options)
    .pipe(catchError(this.errorHandler))
    .pipe(map((userData: any) => {
      const user: User = {
        id: parseInt(userData?.data?.id),
        email: userData?.data?.attributes,
        name: userData?.data?.attributes?.name,
        last_name: userData?.data?.attributes?.last_name,
        jti: userData?.data?.attributes?.jti,
        profile_id: userData?.data?.attributes?.profile_id,
        role_id: userData?.data?.attributes?.role?.id,
        role: userData?.data?.attributes?.role, 
      }
      return user;
    }));
  }

  updateAvatar(id: any, data: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.put(`${this.apiURL}/users/avatar/${id}`, data, options);
  }

  updateTenantAvatar(id: any, data: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.put(`${this.apiURL}/tenants/update_avatar/${id}`, data, options);
  }

  destroyAvatar(id: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.delete(`${this.apiURL}/users/avatar/${id}`, options);
  }
 
  destroyUser(id: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.delete(`${this.apiURL}/users/${id}`, options);
  }

  // CRUD Roles
  getRoles(data: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.get(`${this.apiURL}/roles?${this.parseQueryParams(data)}`, options)
    .pipe(catchError(this.errorHandler))
    .pipe(map((rolesData: any) => {
      const roles: Role[] = [];
      rolesData.data.forEach((roleData: any) => {
        const role: Role = {
          id: parseInt(roleData?.id),
          name: roleData?.attributes,
          group_id: roleData?.attributes?.group_id,
          permissions: roleData?.attributes?.permissions,
        }
        roles.push(role);
      });
      return roles;
    }));
  }

  getRole(id: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.get(`${this.apiURL}/roles/${id}`, options)
    .pipe(catchError(this.errorHandler))
    .pipe(map((rolesData: any) => {
      const role: Role = {
        id: parseInt(rolesData?.data?.id),
        name: rolesData?.data?.attributes?.name,
        group_id: rolesData?.data?.attributes?.group_id,
        permissions: rolesData?.data?.attributes?.permissions,
      }

      return role;
    }));
  }

  createRole(data: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.post(`${this.apiURL}/roles`, data, options);
  }

  updateRole(id: any, data: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.put(`${this.apiURL}/roles/${id}`, { role: data }, options);
  }

  destroyRole(id: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.delete(`${this.apiURL}/roles/${id}`, options);
  }

  // CRUD Profile

  getProfiles(data: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.get(`${this.apiURL}/profiles?${this.parseQueryParams(data)}`, options)
    .pipe(catchError(this.errorHandler))
    .pipe(map((profilesData: any) => {
      const profiles: Profile[] = [];
      profilesData.data.forEach((profileData: any) => {
        const profile: Profile = {
          id: parseInt(profileData?.id),
          name: profileData?.attributes?.name,
          rfc: profileData?.attributes?.rfc,
          razon_social: profileData?.attributes?.razon_social,
          regimen_fiscal: profileData?.attributes?.regimen_fiscal,
          email: profileData?.attributes?.email,
          phone: profileData?.attributes?.phone,
          address: profileData?.attributes?.address,
          utility_id: profileData?.attributes?.utility_id,
          public_record_id: profileData?.attributes?.public_record_id,
          thsm_company_id: profileData?.attributes?.thsm_company_id,
          logo: profileData?.attributes?.logo
        }
        profiles.push(profile);
      });
      const meta: any = {
        per_page: profilesData?.meta?.params?.per_page,
        total_entries: profilesData?.meta?.pagination?.total_entries,
        current_page: profilesData?.meta?.pagination?.current_page,
      };
      return { data: profiles, meta: meta };
    }));
  }

  getProfile(id: any): Observable<any> {
    const options = this.getHeaders(this.token);    
    return this.httpClient.get(`${this.apiURL}/profiles/${id}`, options)
    .pipe(catchError(this.errorHandler))
    .pipe(map((profileData: any) => {
      const profile: Profile = {
        id: parseInt(profileData?.data?.id),
        name: profileData?.data?.attributes?.name,
        rfc: profileData?.data?.attributes?.rfc,
        razon_social: profileData?.data?.attributes?.razon_social,
        regimen_fiscal: profileData?.data?.attributes?.regimen_fiscal,
        email: profileData?.data?.attributes?.email,
        phone: profileData?.data?.attributes?.phone,
        address: profileData?.data?.attributes?.address,
        utility_id: profileData?.data?.attributes?.utility_id,
        public_record_id: profileData?.data?.attributes?.public_record_id,
        thsm_company_id: profileData?.data?.attributes?.thsm_company_id,
        logo: profileData?.data?.attributes?.logo,
      }
      console.log('profile load ===> ', profile);
      return profile;
    }));
  }

  createProfile(data: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.post(`${this.apiURL}/profiles`, data, options);
  }

  updateProfile(id: any, data: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.put(`${this.apiURL}/profiles/${id}`, data, options);
  }

  destroyProfile(id: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.delete(`${this.apiURL}/profiles/${id}`, options);
  }

  // CRUD Refuel

  getRefuels(data: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.get(`${this.apiURL}/refuels?${this.parseQueryParams(data)}`, options)
    .pipe(catchError(this.errorHandler))
    .pipe(map((refuelsData: any) => {
      const refuels: Refuel[] = [];
      refuelsData.data.forEach((refuelData: any) => {
        const refuel: Refuel = {
          id: refuelData?.id,
          pre_charge: refuelData?.attributes?.pre_charge,
          post_charge: refuelData?.attributes?.post_charge,
          folio: refuelData?.attributes?.folio,
          created_at: refuelData?.attributes?.created_at,
          updated_at: refuelData?.attributes?.updated_at,          
          status: refuelData?.attributes?.status,
          tanque_id: refuelData?.attributes?.tanque_id,
          liters: refuelData?.attributes?.liters,
          tanque: refuelData?.attributes?.tanque,
          purchase_order: refuelData?.attributes?.purchase_order
        }
        refuels.push(refuel);
      });
      const meta: any = {
        per_page: refuelsData?.meta?.params?.per_page,
        total_entries: refuelsData?.meta?.pagination?.total_entries,
        current_page: refuelsData?.meta?.pagination?.current_page,
      };
      return { data: refuels, meta: meta };
    }));
  }

  queryRefuels(data: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.get(`${this.apiURL}/refuels/query?${this.parseQueryParams(data)}`, options)
    .pipe(catchError(this.errorHandler))
    .pipe(map((refuelsData: any) => {
      const refuels: Refuel[] = [];
      refuelsData.data.forEach((refuelData: any) => {
        const refuel: Refuel = {
          id: refuelData?.id,
          pre_charge: refuelData?.attributes?.pre_charge,
          post_charge: refuelData?.attributes?.post_charge,
          folio: refuelData?.attributes?.folio,
          created_at: refuelData?.attributes?.created_at,
          updated_at: refuelData?.attributes?.updated_at,          
          status: refuelData?.attributes?.status,
          tanque_id: refuelData?.attributes?.tanque_id,
          liters: refuelData?.attributes?.liters,
          tanque: refuelData?.attributes?.tanque,
          purchase_order: refuelData?.attributes?.purchase_order
        }
        refuels.push(refuel);
      });
      const meta: any = {
        per_page: refuelsData?.meta?.params?.per_page,
        total_entries: refuelsData?.meta?.pagination?.total_entries,
        current_page: refuelsData?.meta?.pagination?.current_page,
      };
      return { data: refuels, meta: meta };
    }));
  }

  getRefuel(id: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.get(`${this.apiURL}/refuels/${id}`, options)
    .pipe(catchError(this.errorHandler))
    .pipe(map((refuelsData: any) => {
      const refuels: Refuel[] = [];
      refuelsData.data.forEach((refuelData: any) => {
        const refuel: Refuel = {
          id: refuelData?.id,
          pre_charge: refuelData?.attributes?.pre_charge,
          post_charge: refuelData?.attributes?.post_charge,
          folio: refuelData?.attributes?.folio,
          created_at: refuelData?.attributes?.created_at,
          updated_at: refuelData?.attributes?.updated_at,          
          status: refuelData?.attributes?.status,
          tanque_id: refuelData?.attributes?.tanque_id,
          liters: refuelData?.attributes?.liters,
          tanque: refuelData?.attributes?.tanque,
          purchase_order: refuelData?.attributes?.purchase_order
        }
        refuels.push(refuel);
      });
      const meta: any = {
        per_page: refuelsData?.meta?.params?.per_page,
        total_entries: refuelsData?.meta?.pagination?.total_entries,
        current_page: refuelsData?.meta?.pagination?.current_page,
      };
      return { data: refuels, meta: meta };
    }));
  }

  createRefuel(data: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.post(`${this.apiURL}/refuels`, data, options)
    .pipe(catchError(this.errorHandler))
    .pipe(map((refuelsData: any) => {
      const refuels: Refuel[] = [];
      refuelsData.data.forEach((refuelData: any) => {
        const refuel: Refuel = {
          id: refuelData?.id,
          pre_charge: refuelData?.attributes?.pre_charge,
          post_charge: refuelData?.attributes?.post_charge,
          folio: refuelData?.attributes?.folio,
          created_at: refuelData?.attributes?.created_at,
          updated_at: refuelData?.attributes?.updated_at,          
          status: refuelData?.attributes?.status,
          tanque_id: refuelData?.attributes?.tanque_id,
          liters: refuelData?.attributes?.liters,
          tanque: refuelData?.attributes?.tanque,
          purchase_order: refuelData?.attributes?.purchase_order
        }
        refuels.push(refuel);
      });
      const meta: any = {
        per_page: refuelsData?.meta?.params?.per_page,
        total_entries: refuelsData?.meta?.pagination?.total_entries,
        current_page: refuelsData?.meta?.pagination?.current_page,
      };
      return { data: refuels, meta: meta };
    }));
  }

  updateRefuel(id: any, data: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.put(`${this.apiURL}/refuels/${id}`, data, options)
    .pipe(catchError(this.errorHandler))
    .pipe(map((refuelsData: any) => {
      const refuels: Refuel[] = [];
      refuelsData.data.forEach((refuelData: any) => {
        const refuel: Refuel = {
          id: refuelData?.id,
          pre_charge: refuelData?.attributes?.pre_charge,
          post_charge: refuelData?.attributes?.post_charge,
          folio: refuelData?.attributes?.folio,
          created_at: refuelData?.attributes?.created_at,
          updated_at: refuelData?.attributes?.updated_at,          
          status: refuelData?.attributes?.status,
          tanque_id: refuelData?.attributes?.tanque_id,
          liters: refuelData?.attributes?.liters,
          tanque: refuelData?.attributes?.tanque,
          purchase_order: refuelData?.attributes?.purchase_order
        }
        refuels.push(refuel);
      });
      const meta: any = {
        per_page: refuelsData?.meta?.params?.per_page,
        total_entries: refuelsData?.meta?.pagination?.total_entries,
        current_page: refuelsData?.meta?.pagination?.current_page,
      };
      return { data: refuels, meta: meta };
    }));
  }

  destroyRefuel(id: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.delete(`${this.apiURL}/refuels/${id}`, options)
    .pipe(catchError(this.errorHandler))
    .pipe(map((refuelsData: any) => {
      const refuels: Refuel[] = [];
      refuelsData.data.forEach((refuelData: any) => {
        const refuel: Refuel = {
          id: refuelData?.id,
          pre_charge: refuelData?.attributes?.pre_charge,
          post_charge: refuelData?.attributes?.post_charge,
          folio: refuelData?.attributes?.folio,
          created_at: refuelData?.attributes?.created_at,
          updated_at: refuelData?.attributes?.updated_at,          
          status: refuelData?.attributes?.status,
          tanque_id: refuelData?.attributes?.tanque_id,
          liters: refuelData?.attributes?.liters,
          tanque: refuelData?.attributes?.tanque,
          purchase_order: refuelData?.attributes?.purchase_order
        }
        refuels.push(refuel);
      });
      const meta: any = {
        per_page: refuelsData?.meta?.params?.per_page,
        total_entries: refuelsData?.meta?.pagination?.total_entries,
        current_page: refuelsData?.meta?.pagination?.current_page,
      };
      return { data: refuels, meta: meta };
    }));
  }

  // CRUD PurchaseOrders

  getPurchaseOrders(data: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.get(`${this.apiURL}/purchase_orders?${this.parseQueryParams(data)}`, options)
    .pipe(catchError(this.errorHandler))
    .pipe(map((purchaseOrdersData: any) => {
      const purchaseOrders: Order[] = [];
      purchaseOrdersData.data.forEach((poData: any) => {
        const purchaseOrder: Order = {
          id: poData?.id,
          folio: poData?.attributes?.folio,
          status: poData?.attributes?.status,
          liters: poData?.attributes?.liters,
          created_at: poData?.attributes?.created_at,
          updated_at: poData?.attributes?.updated_at,
          price: poData?.attributes?.price,
          settlement_id: poData?.attributes?.settlement_id,
          tanque_id: poData?.attributes?.tanque_id,
          profile_id: poData?.attributes?.profile_id,
          settlement: poData?.attributes?.settlement,
          tanque: poData?.attributes?.tanque,
        }
        purchaseOrders.push(purchaseOrder);
      });
      const meta: any = {
        per_page: purchaseOrdersData?.meta?.params?.per_page,
        total_entries: purchaseOrdersData?.meta?.pagination?.total_entries,
        current_page: purchaseOrdersData?.meta?.pagination?.current_page,
      };
      return { data: purchaseOrders, meta: meta };
    }
    ));
  }

  getPurchaseOrder(id: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.get(`${this.apiURL}/purchase-orders/${id}`, options)
    .pipe(catchError(this.errorHandler))
    .pipe(map((purchaseOrderData: any) => {
      const poData = purchaseOrderData;
      const purchaseOrder: Order = {
        id: poData.id,
        folio: poData.folio,
        status: poData.status,
        liters: poData.liters,
        created_at: poData.created_at,
        updated_at: poData.updated_at,
        price: poData.price,
        settlement_id: poData.settlement_id,
        tanque_id: poData.tanque_id,
        profile_id: poData.profile_id,
        settlement: poData?.attributes?.settlement,
        tanque: poData?.attributes?.tanque,
      }
      return purchaseOrder;
    }));
  }

  createPurchaseOrder(data: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.post(`${this.apiURL}/purchase-orders`, data, options)
    .pipe(catchError(this.errorHandler))
    .pipe(map((purchaseOrderData: any) => {
      const poData = purchaseOrderData;
      const purchaseOrder: Order = {
        id: poData.id,
        folio: poData.folio,
        status: poData.status,
        liters: poData.liters,
        created_at: poData.created_at,
        updated_at: poData.updated_at,
        price: poData.price,
        settlement_id: poData.settlement_id,
        tanque_id: poData.tanque_id,
        profile_id: poData.profile_id,
        settlement: poData?.attributes?.settlement,
        tanque: poData?.attributes?.tanque, 
      }
      return purchaseOrder;
    }));
  }

  updatePurchaseOrder(id: any, data: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.put(`${this.apiURL}/purchase-orders/${id}`, data, options)
    .pipe(catchError(this.errorHandler))
    .pipe(map((purchaseOrderData: any) => {
      const poData = purchaseOrderData;
      const purchaseOrder: Order = {
        id: poData.id,
        folio: poData.folio,
        status: poData.status,
        liters: poData.liters,
        created_at: poData.created_at,
        updated_at: poData.updated_at,
        price: poData.price,
        settlement_id: poData.settlement_id,
        tanque_id: poData.tanque_id,
        profile_id: poData.profile_id,
        settlement: poData?.attributes?.settlement,
        tanque: poData?.attributes?.tanque,
      }
      return purchaseOrder;
    }));
  }

  destroyPurchaseOrder(id: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.delete(`${this.apiURL}/purchase-orders/${id}`, options)
    .pipe(catchError(this.errorHandler))
    .pipe(map((purchaseOrderData: any) => {
      const poData = purchaseOrderData;
      const purchaseOrder: Order = {
        id: poData.id,
        folio: poData.folio,
        status: poData.status,
        liters: poData.liters,
        created_at: poData.created_at,
        updated_at: poData.updated_at,
        price: poData.price,
        settlement_id: poData.settlement_id,
        tanque_id: poData.tanque_id,
        profile_id: poData.profile_id,
        settlement: poData?.attributes?.settlement,
        tanque: poData?.attributes?.tanque,
      }
      return purchaseOrder;
    }));
  }
  // CRUD tenant-invoices

  getTenantInvoices(data: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.get(`${this.apiURL}/tenant_invoices?${this.parseQueryParams(data)}`, options);
  }

  getInvoiceList(data: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.post(`${this.apiURL}/tenant_invoices/list`, data, options);
  }

  getTenantInovice(id: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.get(`${this.apiURL}/tenant_invoices/${id}`, options);
  }

  setInvoiceOpened(data: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.post(`${this.apiURL}/tenant_invoices/opened`, data, options);
  }

  setInvoiceRead(data: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.post(`${this.apiURL}/tenant_invoices/read`, data, options);
  }

  setInvoiceConfirmed(data: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.post(`${this.apiURL}/tenant_invoices/confirmed`, data, options);
  }

  setInvoiceAccepted(data: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.post(`${this.apiURL}/tenant_invoices/accepted`, data, options);
  }
  

  // CRUD invoices

  getInvoices(data: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.get(`${this.apiURL}/invoices?${this.parseQueryParams(data)}`, options);
  }

  getInvoice(id: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.get(`${this.apiURL}/invoices/${id}`, options);
  }

  getInvoiceGlobalLast(data: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.get(`${this.apiURL}/invoices/global/last?${this.parseQueryParams(data)}`, options);
  }

  createInvoice(data: any): Observable<any> {
    const options = this.getHeaders(this.token);
    // Never re-invoice a credit or a concept belonging to another receipt.
    // Keep the receipt totals unchanged: the API owns its balance calculation.
    const payload = Array.isArray(data.Conceptos)
      ? { ...data, Conceptos: data.Conceptos.filter((item: BillItem) =>
          !isBalanceCreditItem(item) && item.cancelled !== true &&
          (data.bill?.id == null || item.bill_id == null || String(item.bill_id) === String(data.bill.id))
        ) }
      : data;
    return this.httpClient.post(`${this.apiURL}/invoices`, payload, options);
  }

  createGenericInvoice(data: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.post(`${this.apiURL}/invoices/generic`, data, options);
  }

  createInvoiceComplement(data: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.post(`${this.apiURL}/invoices/complemento`, data, options);
  }

  createInvoiceGlobal(data: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.post(`${this.apiURL}/invoices/global`, data, options);
  }

  createInvoiceNotaCredito(data: any): Observable<any> {
    const options = this.getHeaders(this.token);
    // return this.httpClient.post(`http://localhost:3000/api/v1/invoices/nota_credito`, data);
    return this.httpClient.post(`${this.apiURL}/invoices/nota_credito`, data, options);
  }

  getInvoiceStatus(id: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.get(`${this.apiURL}/invoices/status/${id}`, options);
  }

  updateInvoice(id: any, data: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.put(`${this.apiURL}/invoices/${id}`, data, options);
  }

  saveInvoicePdf(id: any, data: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.put(`${this.apiURL}/invoices/save_pdf/${id}`, data, options);
  }

  destroyInvoice(id: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.delete(`${this.apiURL}/invoices/${id}`, options);
  }

  cancelInvoice(data: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.post(`${this.apiURL}/invoices/cancel`, data, options);
  }

  getInvoicesQuery(data: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.get(`${this.apiURL}/invoices/query?${this.parseQueryParams(data)}`, options);
  }

  downloadInvoicesCsv(data: any, query: InvoiceCsvQuery = 'query'): Observable<Blob> {
    const options = this.getHeaders(this.token);
    return this.httpClient.get(`${this.apiURL}/invoices/${query}?${this.parseQueryParams(data)}`, {
      headers: options.headers.set('Accept', 'text/csv'),
      responseType: 'blob'
    });
  }

  getInvoicesQueryGeneric(data: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.get(`${this.apiURL}/invoices/query_generic?${this.parseQueryParams(data)}`, options);
  }

  getInvoicesQueryGenericUuid(data: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.get(`${this.apiURL}/invoices/query_generic_uuid?${this.parseQueryParams(data)}`, options);
  }

  getInvoicesByUnit(id: any, data: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.get(`${this.apiURL}/invoices/unit/${id}?${this.parseQueryParams(data)}`, options);
  }

  getInvoicesQueryGlobal(data: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.get(`${this.apiURL}/invoices/query_global?${this.parseQueryParams(data)}`, options);
  }

  getInvoicesQueryCanceled(data: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.get(`${this.apiURL}/invoices/query_canceled?${this.parseQueryParams(data)}`, options);
  }

  getInvoiceQueryCancel(data: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.get(`${this.apiURL}/invoices/query/cancel?${this.parseQueryParams(data)}`, options);
  }

  getInvoiceBySettlement(id: any, data: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.get(`${this.apiURL}/invoices/${id}/settlement?${this.parseQueryParams(data)}`, options);
  }

  getInvoiceByBuilding(id: any, data: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.get(`${this.apiURL}/invoices/${id}/building?${this.parseQueryParams(data)}`, options);
  }

  getInvoiceByFloor(id: any, data: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.get(`${this.apiURL}/invoices/${id}/floor?${this.parseQueryParams(data)}`, options);
  }

  getInvoiceByUnit(id: any, data: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.get(`${this.apiURL}/invoices/${id}/unit?${this.parseQueryParams(data)}`, options);
  }

  getBillByInvoice(id: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.get(`${this.apiURL}/bills/${id}/invoice`, options);
  }

  getInvoiceByBill(id: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.get(`${this.apiURL}/invoices/folio/${id}`, options);
  }

  getGlobalInvoiceBySettlement(data: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.get(`${this.apiURL}/invoices/global_settlement?${this.parseQueryParams(data)}`, options);
  }

  getGlobalRelatedBills(id: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.get(`${this.apiURL}/invoices/global/related/${id}`, options);
  }

  // CRUD Subscriptions

  getSubscriptions(id: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.get(`${this.apiURL}/subscriptions/${id}`, options);
  }

  getSubscription(id: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.get(`${this.apiURL}/subscriptions/unit/${id}`, options)
    .pipe(catchError(this.errorHandler))
    .pipe(map((subscriptionData: any) => {
      let suscriptions: any = [];
      subscriptionData.data.forEach((subscriptionData: any) => {        
        const subscription: any = {
          id: subscriptionData?.id,
          unit_id: subscriptionData?.attributes?.unit_id,
          utility: subscriptionData?.attributes?.utility,
          utility_id: subscriptionData?.attributes?.utility_id,
          utility_setting: subscriptionData?.attributes?.utility_setting,
        }
        console.log('subscription ===> ', subscription);
        suscriptions.push(subscription);
      });
      console.log('suscriptions ===> ', suscriptions);
      return suscriptions;      
    }));
  }

  createSubscription(data: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.post(`${this.apiURL}/subscriptions`, data, options);
  }

  // CRUD Technicians

  getTechnicians(data: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.get(`${this.apiURL}/technicians?${this.parseQueryParams(data)}`, options);
  }

  getTechnician(id: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.get(`${this.apiURL}/technicians/${id}`, options);
  }

  createTechnician(data: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.post(`${this.apiURL}/technicians`, data, options);
  }

  updateTechnician(id: any, data: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.put(`${this.apiURL}/technicians/${id}`, data, options);
  }

  destroyTechnician(id: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.delete(`${this.apiURL}/technicians/${id}`, options);
  }

  // CRUD Reading

  getReadings(data: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.get(`${this.apiURL}/readings?${this.parseQueryParams(data)}`, options);
  }

  getReading(id: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.get(`${this.apiURL}/readings/${id}`, options);
  }

  getReadingByTanque(id: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.get(`${this.apiURL}/readings/tanque/${id}`, options);
  }

  getWorkedByProfile(data: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.get(`${this.apiURL}/readings/worked-by-profile?${this.parseQueryParams(data)}`, options);
  }

  getWorkedBySettlement(data: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.get(`${this.apiURL}/readings/worked-by-settlement?${this.parseQueryParams(data)}`, options);
  }

  createReading(data: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.post(`${this.apiURL}/readings`, data, options);
  }

  updateReading(id: any, data: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.put(`${this.apiURL}/readings/${id}`, data, options);
  }

  destroyReading(id: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.delete(`${this.apiURL}/readings/${id}`, options);
  }

  // CRUD Ability

  getAbilities(data: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.get(`${this.apiURL}/abilities?${this.parseQueryParams(data)}`, options);
  }

  getAbility(id: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.get(`${this.apiURL}/abilities/${id}`, options);
  }

  createAbility(data: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.post(`${this.apiURL}/abilities`, data, options);
  }

  updateAbility(id: any, data: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.put(`${this.apiURL}/abilities/${id}`, data, options);
  }

  destroyAbility(id: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.delete(`${this.apiURL}/abilities/${id}`, options);
  }

  // CRUD Bills
  // getBills(data: any): Observable<any> {
    // const options = this.getHeaders(this.token);
    // return this.httpClient.get(`${this.apiURL}/bills?${this.parseQueryParams(data)}`, options);
  // }

  createBill(data: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.post(`${this.apiURL}/bills`, data, options);
  }

  createAdvancedBill(data: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.post(`${this.apiURL}/bills/advanced-bill`, data, options);
  }

  updateBill(id: any, data: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.put(`${this.apiURL}/bills/${id}`, data, options);
  }

  publishBill(id: any, data: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.put(`${this.apiURL}/bills/publish/${id}`, data, options)
    .pipe(catchError(this.errorHandler))
    .pipe(map((billData: any) => {

      console.log('billData', billData);

      const billItems: BillItem[] = [];
      billData?.included.map((billItemData: any) => {
        const billItem: BillItem = {
          id: billItemData?.id,
          bill_id: billItemData?.attributes?.bill_id, 
          item_type: billItemData?.attributes?.item_type, 
          item_id: billItemData?.attributes?.item_id,
          quantity: billItemData?.attributes?.quantity,
          price: billItemData?.attributes?.price,
          total: billItemData?.attributes?.total,
          sku: billItemData?.attributes?.sku,
          discount_amount: billItemData?.attributes?.discount_amount,
          net_price: billItemData?.attributes?.net_price,
          tax_rate: billItemData?.attributes?.tax_rate,
          tax_amount: billItemData?.attributes?.tax_amount,
          gas_meter_read: {
            id: billItemData?.attributes?.gas_meter_read?.data?.id,
            value: billItemData?.attributes?.gas_meter_read?.data?.attributes?.value,
            unit_id: billItemData?.attributes?.gas_meter_read?.data?.attributes?.unit_id,
            ocr_value: billItemData?.attributes?.gas_meter_read?.data?.attributes?.ocr_value,
            value_prev: billItemData?.attributes?.gas_meter_read?.data?.attributes?.value_prev,
            status: billItemData?.attributes?.gas_meter_read?.data?.attributes?.status,
            motive: billItemData?.attributes?.gas_meter_read?.data?.attributes?.motive,
            gas_meter_id: billItemData?.attributes?.gas_meter_read?.data?.attributes?.gas_meter_id,
            consumo_m3: billItemData?.attributes?.gas_meter_read?.data?.attributes?.consumo_m3,
            consumo_lt: billItemData?.attributes?.gas_meter_read?.data?.attributes?.consumo_lt,
            consumo_periodo: billItemData?.attributes?.gas_meter_read?.data?.attributes?.consumo_periodo,
            discount: billItemData?.attributes?.gas_meter_read?.data?.attributes?.discount,
            consumo_total: billItemData?.attributes?.gas_meter_read?.data?.attributes?.consumo_total,
            gas_price: billItemData?.attributes?.gas_meter_read?.data?.attributes?.gas_price,
            bill_item_id: billItemData?.attributes?.gas_meter_read?.data?.attributes?.bill_item_id,
            photo: billItemData?.attributes?.gas_meter_read?.data?.attributes?.photo
          },
          cancelled: billItemData?.attributes?.cancelled,
        }
        billItems.push(billItem);
      });

      const bill: Bill = {
        id: billData?.data?.id,
        reference: billData?.data?.attributes?.reference,
        description: billData?.data?.attributes?.description,
        status: billData?.data?.attributes?.status,
        consumo_m3: billData?.data?.attributes?.consumo_m3,
        consumo_lt: billData?.data?.attributes?.consumo_lt,
        consumo_periodo: billData?.data?.attributes?.consumo_periodo,
        sub_total: billData?.data?.attributes?.sub_total,
        total: billData?.data?.attributes?.total,
        days: billData?.data?.attributes?.days,
        read_prev: billData?.data?.attributes?.read_prev,
        read_curr: billData?.data?.attributes?.read_curr,
        discount: billData?.data?.attributes?.discount,
        periodo_inicio: billData?.data?.attributes?.periodo_inicio,
        periodo_fin: billData?.data?.attributes?.periodo_fin,
        due_date: billData?.data?.attributes?.due_date,
        paid_date: billData?.data?.attributes?.paid_date,
        created_at: billData?.data?.attributes?.created_at,
        updated_at: billData?.data?.attributes?.updated_at,
        subscription_id: billData?.data?.attributes?.subscription_id,
        admin_price: billData?.data?.attributes?.admin_price,
        interests: billData?.data?.attributes?.interests,
        taxes: billData?.data?.attributes?.taxes,
        service_sub_total: billData?.data?.attributes?.service_sub_total,
        additional_services: billData?.data?.attributes?.additional_services,
        unit_id: billData?.data?.attributes?.unit_id,
        recargos: billData?.data?.attributes?.recargos,
        consumo_total: billData?.data?.attributes?.consumo_total,
        state: billData?.data?.attributes?.state,
        pdf: billData?.data?.attributes?.pdf,
        invoiced: billData?.data?.attributes?.invoiced,
        invoice_id: billData?.data?.attributes?.invoice_id,
        adv: billData?.data?.attributes?.adv,
        retry: billData?.data?.attributes?.retry,
        gas_price: billData?.data?.attributes?.gas_price,
        cancel_interests: billData?.data?.attributes?.cancel_interests,
        cancel_recargos: billData?.data?.attributes?.cancel_recargos,
        merchant_id: billData?.data?.attributes?.merchant_id,
        sales_order: billData?.data?.attributes?.sales_order,
        settlement: billData?.data?.attributes?.settlement,
        unit: billData?.data?.attributes?.unit,
        tenant: billData?.data?.attributes?.tenant,
        bill_items: billItems
      }
      return bill;
    }));

  }

  unpublishBill(id: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.get(`${this.apiURL}/bills/unpublish/${id}`, options)
    .pipe(catchError(this.errorHandler))
    .pipe(map((billData: any) => {

      console.log('billData', billData);

      const billItems: BillItem[] = [];
      billData?.included.map((billItemData: any) => {
        const billItem: BillItem = {
          id: billItemData?.id,
          bill_id: billItemData?.attributes?.bill_id, 
          item_type: billItemData?.attributes?.item_type, 
          item_id: billItemData?.attributes?.item_id,
          quantity: billItemData?.attributes?.quantity,
          price: billItemData?.attributes?.price,
          total: billItemData?.attributes?.total,
          sku: billItemData?.attributes?.sku,
          discount_amount: billItemData?.attributes?.discount_amount,
          net_price: billItemData?.attributes?.net_price,
          tax_rate: billItemData?.attributes?.tax_rate,
          tax_amount: billItemData?.attributes?.tax_amount,
          gas_meter_read: {
            id: billItemData?.attributes?.gas_meter_read?.data?.id,
            value: billItemData?.attributes?.gas_meter_read?.data?.attributes?.value,
            unit_id: billItemData?.attributes?.gas_meter_read?.data?.attributes?.unit_id,
            ocr_value: billItemData?.attributes?.gas_meter_read?.data?.attributes?.ocr_value,
            value_prev: billItemData?.attributes?.gas_meter_read?.data?.attributes?.value_prev,
            status: billItemData?.attributes?.gas_meter_read?.data?.attributes?.status,
            motive: billItemData?.attributes?.gas_meter_read?.data?.attributes?.motive,
            gas_meter_id: billItemData?.attributes?.gas_meter_read?.data?.attributes?.gas_meter_id,
            consumo_m3: billItemData?.attributes?.gas_meter_read?.data?.attributes?.consumo_m3,
            consumo_lt: billItemData?.attributes?.gas_meter_read?.data?.attributes?.consumo_lt,
            consumo_periodo: billItemData?.attributes?.gas_meter_read?.data?.attributes?.consumo_periodo,
            discount: billItemData?.attributes?.gas_meter_read?.data?.attributes?.discount,
            consumo_total: billItemData?.attributes?.gas_meter_read?.data?.attributes?.consumo_total,
            gas_price: billItemData?.attributes?.gas_meter_read?.data?.attributes?.gas_price,
            bill_item_id: billItemData?.attributes?.gas_meter_read?.data?.attributes?.bill_item_id,
            photo: billItemData?.attributes?.gas_meter_read?.data?.attributes?.photo
          },
          cancelled: billItemData?.attributes?.cancelled,
        }
        billItems.push(billItem);
      });

      const bill: Bill = {
        id: billData?.data?.id,
        reference: billData?.data?.attributes?.reference,
        description: billData?.data?.attributes?.description,
        status: billData?.data?.attributes?.status,
        consumo_m3: billData?.data?.attributes?.consumo_m3,
        consumo_lt: billData?.data?.attributes?.consumo_lt,
        consumo_periodo: billData?.data?.attributes?.consumo_periodo,
        sub_total: billData?.data?.attributes?.sub_total,
        total: billData?.data?.attributes?.total,
        days: billData?.data?.attributes?.days,
        read_prev: billData?.data?.attributes?.read_prev,
        read_curr: billData?.data?.attributes?.read_curr,
        discount: billData?.data?.attributes?.discount,
        periodo_inicio: billData?.data?.attributes?.periodo_inicio,
        periodo_fin: billData?.data?.attributes?.periodo_fin,
        due_date: billData?.data?.attributes?.due_date,
        paid_date: billData?.data?.attributes?.paid_date,
        created_at: billData?.data?.attributes?.created_at,
        updated_at: billData?.data?.attributes?.updated_at,
        subscription_id: billData?.data?.attributes?.subscription_id,
        admin_price: billData?.data?.attributes?.admin_price,
        interests: billData?.data?.attributes?.interests,
        taxes: billData?.data?.attributes?.taxes,
        service_sub_total: billData?.data?.attributes?.service_sub_total,
        additional_services: billData?.data?.attributes?.additional_services,
        unit_id: billData?.data?.attributes?.unit_id,
        recargos: billData?.data?.attributes?.recargos,
        consumo_total: billData?.data?.attributes?.consumo_total,
        state: billData?.data?.attributes?.state,
        pdf: billData?.data?.attributes?.pdf,
        invoiced: billData?.data?.attributes?.invoiced,
        invoice_id: billData?.data?.attributes?.invoice_id,
        adv: billData?.data?.attributes?.adv,
        retry: billData?.data?.attributes?.retry,
        gas_price: billData?.data?.attributes?.gas_price,
        cancel_interests: billData?.data?.attributes?.cancel_interests,
        cancel_recargos: billData?.data?.attributes?.cancel_recargos,
        merchant_id: billData?.data?.attributes?.merchant_id,
        sales_order: billData?.data?.attributes?.sales_order,
        settlement: billData?.data?.attributes?.settlement,
        unit: billData?.data?.attributes?.unit,
        tenant: billData?.data?.attributes?.tenant,
        bill_items: billItems
      }
      return bill;
    }));
  }

  cancelBill(id: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.get(`${this.apiURL}/bills/${id}/cancel`, options);
  }

  getPreviousBill(id: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.get(`${this.apiURL}/bills/previous/${id}`, options)
    .pipe(catchError(this.errorHandler))
    .pipe(map((billData: any) => {
      if (!billData?.data) {
        return null;
      }

      const billItems: BillItem[] = [];
      billData?.included.map((billItemData: any) => {
        const billItem: BillItem = {
          id: billItemData?.id,
          bill_id: billItemData?.attributes?.bill_id, 
          item_type: billItemData?.attributes?.item_type, 
          item_id: billItemData?.attributes?.item_id,
          quantity: billItemData?.attributes?.quantity,
          price: billItemData?.attributes?.price,
          total: billItemData?.attributes?.total,
          sku: billItemData?.attributes?.sku,
          discount_amount: billItemData?.attributes?.discount_amount,
          net_price: billItemData?.attributes?.net_price,
          tax_rate: billItemData?.attributes?.tax_rate,
          tax_amount: billItemData?.attributes?.tax_amount,
          gas_meter_read: {
            id: billItemData?.attributes?.gas_meter_read?.data?.id,
            value: billItemData?.attributes?.gas_meter_read?.data?.attributes?.value,
            unit_id: billItemData?.attributes?.gas_meter_read?.data?.attributes?.unit_id,
            ocr_value: billItemData?.attributes?.gas_meter_read?.data?.attributes?.ocr_value,
            value_prev: billItemData?.attributes?.gas_meter_read?.data?.attributes?.value_prev,
            status: billItemData?.attributes?.gas_meter_read?.data?.attributes?.status,
            motive: billItemData?.attributes?.gas_meter_read?.data?.attributes?.motive,
            gas_meter_id: billItemData?.attributes?.gas_meter_read?.data?.attributes?.gas_meter_id,
            consumo_m3: billItemData?.attributes?.gas_meter_read?.data?.attributes?.consumo_m3,
            consumo_lt: billItemData?.attributes?.gas_meter_read?.data?.attributes?.consumo_lt,
            consumo_periodo: billItemData?.attributes?.gas_meter_read?.data?.attributes?.consumo_periodo,
            discount: billItemData?.attributes?.gas_meter_read?.data?.attributes?.discount,
            consumo_total: billItemData?.attributes?.gas_meter_read?.data?.attributes?.consumo_total,
            gas_price: billItemData?.attributes?.gas_meter_read?.data?.attributes?.gas_price,
            bill_item_id: billItemData?.attributes?.gas_meter_read?.data?.attributes?.bill_item_id,
            photo: billItemData?.attributes?.gas_meter_read?.data?.attributes?.photo
          },
          cancelled: billItemData?.attributes?.cancelled,
        }
        billItems.push(billItem);
      });

      const bill: Bill = {
        id: billData?.data?.id,
        reference: billData?.data?.attributes?.reference,
        description: billData?.data?.attributes?.description,
        status: billData?.data?.attributes?.status,
        consumo_m3: billData?.data?.attributes?.consumo_m3,
        consumo_lt: billData?.data?.attributes?.consumo_lt,
        consumo_periodo: billData?.data?.attributes?.consumo_periodo,
        sub_total: billData?.data?.attributes?.sub_total,
        total: billData?.data?.attributes?.total,
        days: billData?.data?.attributes?.days,
        read_prev: billData?.data?.attributes?.read_prev,
        read_curr: billData?.data?.attributes?.read_curr,
        discount: billData?.data?.attributes?.discount,
        periodo_inicio: billData?.data?.attributes?.periodo_inicio,
        periodo_fin: billData?.data?.attributes?.periodo_fin,
        due_date: billData?.data?.attributes?.due_date,
        paid_date: billData?.data?.attributes?.paid_date,
        created_at: billData?.data?.attributes?.created_at,
        updated_at: billData?.data?.attributes?.updated_at,
        subscription_id: billData?.data?.attributes?.subscription_id,
        admin_price: billData?.data?.attributes?.admin_price,
        interests: billData?.data?.attributes?.interests,
        taxes: billData?.data?.attributes?.taxes,
        service_sub_total: billData?.data?.attributes?.service_sub_total,
        additional_services: billData?.data?.attributes?.additional_services,
        unit_id: billData?.data?.attributes?.unit_id,
        recargos: billData?.data?.attributes?.recargos,
        consumo_total: billData?.data?.attributes?.consumo_total,
        state: billData?.data?.attributes?.state,
        pdf: billData?.data?.attributes?.pdf,
        invoiced: billData?.data?.attributes?.invoiced,
        invoice_id: billData?.data?.attributes?.invoice_id,
        adv: billData?.data?.attributes?.adv,
        retry: billData?.data?.attributes?.retry,
        gas_price: billData?.data?.attributes?.gas_price,
        cancel_interests: billData?.data?.attributes?.cancel_interests,
        cancel_recargos: billData?.data?.attributes?.cancel_recargos,
        merchant_id: billData?.data?.attributes?.merchant_id,
        sales_order: billData?.data?.attributes?.sales_order,
        settlement: billData?.data?.attributes?.settlement,
        unit: billData?.data?.attributes?.unit,
        tenant: billData?.data?.attributes?.tenant,
        bill_items: billItems
      }
      return bill;
    }));
  }

  // getBillsByUtility(data: any): Observable<any> {
    // const options = this.getHeaders(this.token);
    // return this.httpClient.get(`${this.apiURL}/bills/utility/?${this.parseQueryParams(data)}`, options);
  // }

  getBillsByUtilitySummary(data: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.get(`${this.apiURL}/bills/paid/?${this.parseQueryParams(data)}`, options);
  }

  // getBillsByProfile(data: any): Observable<any> {
    // const options = this.getHeaders(this.token);
    // return this.httpClient.get(`${this.apiURL}/bills/profile?${this.parseQueryParams(data)}`, options);
  // }

  updateFailedPayment(id: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.get(`${this.apiURL}/bills/failed/${id}`, options);
  }

  destroyBill(id: any): Observable<any> {
    return this.httpClient.get(`${this.apiURL}/bills/cancel/${id}`);
  }

  getAccountStatementBySettlement(data: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.get(`${this.apiURL}/bills/settlement-statement?${this.parseQueryParams(data)}`, options);
  }

  updateBillPaidDate(id: any, data: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.put(`${this.apiURL}/bills/update_date/${id}`, data, options);
  }

  // CRUD Zones

  getZones(data: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.get(`${this.apiURL}/zones?${this.parseQueryParams(data)}`, options);
  }

  getZone(id: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.get(`${this.apiURL}/zones/${id}`, options);
  }

  createZone(data: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.post(`${this.apiURL}/zones`, data, options);
  }

  updateZone(id: any, data: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.put(`${this.apiURL}/zones/${id}`, data, options);
  }

  destroyZone(id: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.delete(`${this.apiURL}/zones/${id}`, options);
  }

  addZoneToUtility(data: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.post(`${this.apiURL}/zones/add-to-utility`, data, options);
  }

  // getInvoicableBills(data: any): Observable<any> {
    // const options = this.getHeaders(this.token);
    // return this.httpClient.get(`${this.apiURL}/bills/invoiceable?${this.parseQueryParams(data)}`, options);
  // }

  getSettlementInvoices(id: any, data: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.get(`${this.apiURL}/bills/settlement/invoice/${id}?${this.parseQueryParams(data)}`, options);
  }

  // unit_subscriptions_last(id: any): Observable<any> {
    // const options = this.getHeaders(this.token);
    // return this.httpClient.get(`${this.apiURL}/units/bills/last/${id}`, options);
  // }

  getTransaction(id: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.get(`${this.apiURL}/transactions/${id}`, options);
  }

  getTransactionBill(id: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.get(`${this.apiURL}/transactions/${id}`, options);
  }

  getTransactionByBill(id: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.get(`${this.apiURL}/transactions/bill/${id}`, options);
  }

  getTransactionByBillId(id: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return timer(7000).pipe(
      switchMap(() => this.httpClient.get(`${this.apiURL}/transactions/bills/${id}`, options))
    ); 
  }

  createTransaction(data: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.post(`${this.apiURL}/transactions/local`, data, options);
  }

  // ID = transaction_id
  updateTransaction(id: any, data: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.put(`${this.apiURL}/transactions/${id}`, data, options);
  }

  getTransactionsQuery(data: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.get(`${this.apiURL}/transactions/query?${this.parseQueryParams(data)}`, options);
  }

  getTransactionsBySettlement(id: any, data: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.get(`${this.apiURL}/transactions/${id}/settlement?${this.parseQueryParams(data)}`, options);
  }

  getTransactionsByBuilding(id: any, data: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.get(`${this.apiURL}/transactions/${id}/building?${this.parseQueryParams(data)}`, options);
  }

  getTransactionsByFloor(id: any, data: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.get(`${this.apiURL}/transactions/${id}/floor?${this.parseQueryParams(data)}`, options);
  }

  getTransactionsByUnit(id: any, data: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.get(`${this.apiURL}/transactions/${id}/unit?${this.parseQueryParams(data)}`, options);
  }

  getLastTransactionByUnit(id: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.get(`${this.apiURL}/transactions/last/${id}`, options);
  }
  
  login(data: any): Observable<any> {
    let login = this.httpClient.post(`${this.apiURL}/users/login`, data);

    return login;
  }

  signup(data: any): Observable<any> {
    return this.httpClient.post(`${this.apiURL}/users/signup`, data);
  }

  tenantLogin(data: any): Observable<any> {
    return this.httpClient.post(`${this.apiURL}/tenants/login`, data);
  }

  verifyEmail(data: any): Observable<any> {
    return this.httpClient.get(`${this.apiURL}/users/confirmation?confirmation_token=${data}`, data);
  }

  recoverPassword(data: any): Observable<any> {
    let payload: any = {
      user: {
        email: data,
      }      
    };

    return this.httpClient.post(`${this.apiURL}/users/password`, payload);
  }

  confirmReset(data: any): Observable<any> {
    return this.httpClient.get(`${this.apiURL}/users/password/edit?reset_password_token=${data}`);
  }

  uploadReceipt(id: any, data: any): Observable<any> {
    return this.httpClient.put(`${this.apiURL}/bills/upload_receipt/${id}`, data);
  }

  saveReceipt(id: any, data: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.put(`${this.apiURL}/bills/save_receipt/${id}`, data, options);
  }

  private buildPdfPayload(data: any): any {
    if (!data?.bill) {
      return data;
    }

    const pdfDiscount = buildReceiptDiscountFields(
      data?.unitData,
      data?.settlementData ?? data?.buildingData
    );
    const preparePdfItem = (item: BillItem) => withBalanceCreditLabel(withReconnectionFeeLabel(item));
    const pdfBillItems = data.bill?.bill_items?.map(preparePdfItem);
    const pdfItems = (data.bill_items ?? data.bill?.bill_items)?.map(preparePdfItem);
    const pdfBill = {
      ...data.bill,
      ...(pdfBillItems ? { bill_items: pdfBillItems } : {})
    };

    delete pdfBill.discount;
    delete pdfBill.discount_type;
    delete pdfBill.discount_type_id;
    Object.assign(pdfBill, pdfDiscount ?? {});

    return {
      ...data,
      ...(pdfItems ? { bill_items: pdfItems } : {}),
      bill: pdfBill
    };
  }

  createReceipt(data: any): Observable<any> {
    const receiptData = this.buildPdfPayload(data);

    let options = {
      headers: new HttpHeaders({
        'Content-Type': 'text/plain; charset=utf-8',
        'Accept': 'application/pdf'
      }),
      responseType: 'text' as const,
      body: receiptData
    };

    return this.httpClient.post(`${this.receiptURL}/recibos`, options);
  }

  downloadInvoice(data: any): Observable<any> {
    const invoiceData = this.buildPdfPayload(data);

    let options = {
      headers: new HttpHeaders({
        'Content-Type': 'text/plain; charset=utf-8',
        'Accept': 'application/pdf'
      }),
      responseType: 'text' as const,
      body: invoiceData
    };

    return this.httpClient.post(`${this.receiptURL}/invoice`, options);
    // return this.httpClient.post(`http://localhost:3100/invoice`, options);
  }

  downloadGenericInvoice(data: any): Observable<any> {
    let options = {
      headers: new HttpHeaders({
        'Content-Type': 'text/plain; charset=utf-8',
        'Accept': 'application/pdf'
      }),
      responseType: 'text' as const,
      body: data
    };

    return this.httpClient.post(`${this.receiptURL}/generic`, options);
    // return this.httpClient.post(`http://localhost:3100/generic`, options);
  }

  downloadComplement(data: any): Observable<any> {
    let options = {
      headers: new HttpHeaders({
        'Content-Type': 'text/plain; charset=utf-8',
        'Accept': 'application/pdf'
      }),
      responseType: 'text' as const,
      body: data
    };

    return this.httpClient.post(`${this.receiptURL}/complement`, options);
    // return this.httpClient.post(`http://localhost:3100/complement`, options);
  }

  downloadGlobal(data: any): Observable<any> {
    let options = {
      headers: new HttpHeaders({
        'Content-Type': 'text/plain; charset=utf-8',
        'Accept': 'application/pdf'
      }),
      responseType: 'text' as const,
      body: data
    };

    return this.httpClient.post(`${this.receiptURL}/global`, options);
    // return this.httpClient.post(`http://localhost:3100/global`, options);
  }

  downloadNota(data: any): Observable<any> {
    let options = {
      headers: new HttpHeaders({
        'Content-Type': 'text/plain; charset=utf-8',
        'Accept': 'application/pdf'
      }),
      responseType: 'text' as const,
      body: data
    };

    return this.httpClient.post(`${this.receiptURL}/notacredit`, options);
    // return this.httpClient.post(`http://localhost:3100/notacredit`, options);
  }

  downloadPaymentReceipt(data: any): Observable<any> {
    let options = {
      headers: new HttpHeaders({
        'Content-Type': 'text/plain; charset=utf-8',
        'Accept': 'application/pdf'
      }),
      responseType: 'text' as const,
      body: data
    };

    return this.httpClient.post(`${this.receiptURL}/recibo`, options);
    // return this.httpClient.post(`http://localhost:3100/recibo`, options);
  }

  createAcuseCancel(data: any): Observable<any> {
    let options = {
      headers: new HttpHeaders({
        'Content-Type': 'text/plain; charset=utf-8',
        'Accept': 'application/pdf'
      }),
      responseType: 'text' as const,
      body: data
    };

    return this.httpClient.post(`${this.receiptURL}/acuse`, options);
    // return this.httpClient.post(`http://localhost:3100/acuse`, options);
  }

  reportUnitStatement(data: any): Observable<any> {
    let options = {
      headers: new HttpHeaders({
        'Content-Type': 'text/plain; charset=utf-8',
        'Accept': 'application/pdf'
      }),
      responseType: 'text' as const,
      body: data
    };

    return this.httpClient.post(`${this.receiptURL}/reports/edo-cuenta`, options);
  }

  // CRUD Balances

  getBalances(data: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.get(`${this.apiURL}/balances?${this.parseQueryParams(data)}`, options)
    .pipe(catchError(this.errorHandler))
    .pipe(map((balancesData: any) => {
      const balances: Balance[] = [];
      balancesData?.data.map((balanceData: any) => {
        const balance: Balance = {
          id: balanceData?.id,
          unit: balanceData?.attributes?.unit,
          settlement: balanceData?.attributes?.settlement,
          amount: balanceData?.attributes?.amount
        }
        balances.push(balance);
      });
      const meta: any = {
        per_page: balancesData?.meta?.params?.per_page,
        total_entries: balancesData?.meta?.pagination?.total_entries,
        current_page: balancesData?.meta?.pagination?.current_page,
        total_pages: balancesData?.meta?.pagination?.total_pages,
      };
      return { data: balances, meta };
    }
    ));
  }

  getBalance(id: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.get(`${this.apiURL}/balances/${id}`, options);
  }

  // getBalancesByProfile(data: any): Observable<any> {
    // const options = this.getHeaders(this.token);
    // return this.httpClient.get(`${this.apiURL}/bills/profile?${this.parseQueryParams(data)}`, options);
  // }

  getBalancesBySettlement(id: any, data: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.get(`${this.apiURL}/balances/${id}/settlement?${this.parseQueryParams(data)}`, options);
  }

  getBalancesByBuilding(id: any, data: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.get(`${this.apiURL}/balances/${id}/building?${this.parseQueryParams(data)}`, options);
  }

  getBalancesByFloor(id: any, data: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.get(`${this.apiURL}/balances/${id}/floor?${this.parseQueryParams(data)}`, options);
  }

  getBalancesByUnit(unitId: any, data: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.get(`${this.apiURL}/balances/unit/${unitId}?${this.parseQueryParams(data)}`, options);
  }

  searchInquilinos(data: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.get(`${this.apiURL}/tenants?${this.parseQueryParams(data)}`, options);
  }

  // CRUD Gas Meter

  getGasMeters(data: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.get(`${this.apiURL}/gas_meters?${this.parseQueryParams(data)}`, options);
  }

  getGasMeter(id: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.get(`${this.apiURL}/gas_meters/${id}`, options);
  }

  createGasMeter(data: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.post(`${this.apiURL}/gas_meters`, data, options);
  }

  updateGasMeter(id: any, data: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.put(`${this.apiURL}/gas_meters/${id}`, data, options);
  }

  destroyGasMeter(id: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.delete(`${this.apiURL}/gas_meters/${id}`, options);
  }

  getGasMeterReadings(data: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.get(`${this.apiURL}/gas_meter_reads?${this.parseQueryParams(data)}`, options);
  }

  getGasMeterReading(id: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.get(`${this.apiURL}/gas_meter_reads/${id}`, options);
  }

  createGasMeterRead(data: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.post(`${this.apiURL}/gas_meter_reads/custom`, data, options)
    .pipe(catchError(this.errorHandler))
    .pipe(map((gasMeterReadData: any) => {
      const gasMeterRead: GasMeterRead = {
        id: gasMeterReadData?.data?.id,
        value: gasMeterReadData?.data?.attributes?.value,
        unit_id: gasMeterReadData?.data?.attributes?.unit_id,
        ocr_value: gasMeterReadData?.data?.attributes?.ocr_value,
        value_prev: gasMeterReadData?.data?.attributes?.value_prev,
        status: gasMeterReadData?.data?.attributes?.status,
        motive: gasMeterReadData?.data?.attributes?.motive,
        gas_meter_id: gasMeterReadData?.data?.attributes?.gas_meter_id,
        consumo_m3: gasMeterReadData?.data?.attributes?.consumo_m3,
        consumo_lt: gasMeterReadData?.data?.attributes?.consumo_lt,
        consumo_periodo: gasMeterReadData?.data?.attributes?.consumo_periodo,
        discount: gasMeterReadData?.data?.attributes?.discount,
        consumo_total: gasMeterReadData?.data?.attributes?.consumo_total,
        gas_price: gasMeterReadData?.data?.attributes?.gas_price,
        bill_item_id: gasMeterReadData?.data?.attributes?.bill_item_id,
        photo: gasMeterReadData?.data?.attributes?.photo
      }
      return gasMeterRead;
    }
    ));
  }

  updateGasMeterRead(id: any, data: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.put(`${this.apiURL}/gas_meter_reads/${id}`, data, options)
    .pipe(catchError(this.errorHandler))
    .pipe(map((gasMeterReadData: any) => {
      const gasMeterRead: GasMeterRead = {
        id: gasMeterReadData?.data?.id,
        value: gasMeterReadData?.data?.attributes?.value,
        unit_id: gasMeterReadData?.data?.attributes?.unit_id,
        ocr_value: gasMeterReadData?.data?.attributes?.ocr_value,
        value_prev: gasMeterReadData?.data?.attributes?.value_prev,
        status: gasMeterReadData?.data?.attributes?.status,
        motive: gasMeterReadData?.data?.attributes?.motive,
        gas_meter_id: gasMeterReadData?.data?.attributes?.gas_meter_id,
        consumo_m3: gasMeterReadData?.data?.attributes?.consumo_m3,
        consumo_lt: gasMeterReadData?.data?.attributes?.consumo_lt,
        consumo_periodo: gasMeterReadData?.data?.attributes?.consumo_periodo,
        discount: gasMeterReadData?.data?.attributes?.discount,
        consumo_total: gasMeterReadData?.data?.attributes?.consumo_total,
        gas_price: gasMeterReadData?.data?.attributes?.gas_price,
        bill_item_id: gasMeterReadData?.data?.attributes?.bill_item_id,
        photo: gasMeterReadData?.data?.attributes?.photo
      }
      return gasMeterRead;
    }
    ));
  }

  // CRUD Gas Meter Maintenance

  getGasMeterMaintenances(data: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.get(`${this.apiURL}/gas_meter_maintenances?${this.parseQueryParams(data)}`, options);
  }

  getGasMeterMaintenance(id: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.get(`${this.apiURL}/gas_meter_maintenances/${id}`, options);
  }

  createGasMeterMaintenance(data: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.post(`${this.apiURL}/gas_meter_maintenances`, data, options);
  }

  updateGasMeterMaintenance(id: any, data: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.put(`${this.apiURL}/gas_meter_maintenances/${id}`, data, options);
  }

  destroyGasMeterMaintenance(id: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.delete(`${this.apiURL}/gas_meter_maintenances/${id}`, options);
  }

  setPrice(data: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.post(`${this.apiURL}/refuels/set_price`, data, options);
  }

  // CRUD Contract Types

  getContractTypes(data: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.get(`${this.apiURL}/contract_types?${this.parseQueryParams(data)}`, options)
    .pipe(catchError(this.errorHandler))
    .pipe(map((contractTypesData: any) => {
      const contractTypes: ContractType[] = [];
      contractTypesData.data.forEach((contractTypeData: any) => {
        const contractType: ContractType = {
          id: contractTypeData?.id,
          name: contractTypeData?.attributes?.name,
          description: contractTypeData?.attributes?.description,
          validate_type: contractTypeData?.attributes?.validate_type,
          validate_id: contractTypeData?.attributes?.validate_id,
          validate_sms: contractTypeData?.attributes?.validate_sms,
          validate_picture: contractTypeData?.attributes?.validate_picture,
          validate_biometric: contractTypeData?.attributes?.validate_biometric,
          send_email: contractTypeData?.attributes?.send_email,
          is_active: contractTypeData?.attributes?.is_active,
        }
        contractTypes.push(contractType);
      });
      const meta: any = {
        per_page: contractTypesData?.meta?.params?.per_page,
        total_entries: contractTypesData?.meta?.pagination?.total_entries,
        current_page: contractTypesData?.meta?.pagination?.current_page,
      };
      return { data: contractTypes, meta: meta };
    }
    ));
  }

  getContractType(id: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.get(`${this.apiURL}/contract_types/${id}`, options)
    .pipe(catchError(this.errorHandler))
    .pipe(map((contractTypeData: any) => {
      const contractType: ContractType = {
        id: contractTypeData?.id,
        name: contractTypeData?.attributes?.name,
        description: contractTypeData?.attributes?.description,
        validate_type: contractTypeData?.attributes?.validate_type,
        validate_id: contractTypeData?.attributes?.validate_id,
        validate_sms: contractTypeData?.attributes?.validate_sms,
        validate_picture: contractTypeData?.attributes?.validate_picture,
        validate_biometric: contractTypeData?.attributes?.validate_biometric,
        send_email: contractTypeData?.attributes?.send_email,
        is_active: contractTypeData?.attributes?.is_active,
      }
      return contractType;
    }));
  }

  createContractType(data: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.post(`${this.apiURL}/contract_types`, data, options)
    .pipe(catchError(this.errorHandler))
    .pipe(map((contractTypeData: any) => {
      const contractType: ContractType = {
        id: contractTypeData?.id,
        name: contractTypeData?.attributes?.name,
        description: contractTypeData?.attributes?.description,
        validate_type: contractTypeData?.attributes?.validate_type,
        validate_id: contractTypeData?.attributes?.validate_id,
        validate_sms: contractTypeData?.attributes?.validate_sms,
        validate_picture: contractTypeData?.attributes?.validate_picture,
        validate_biometric: contractTypeData?.attributes?.validate_biometric,
        send_email: contractTypeData?.attributes?.send_email,
        is_active: contractTypeData?.attributes?.is_active,
      }
      return contractType;
    }));
  }

  updateContractType(id: any, data: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.put(`${this.apiURL}/contract_types/${id}`, data, options)
    .pipe(catchError(this.errorHandler))
    .pipe(map((contractTypeData: any) => {
      const contractType: ContractType = {
        id: contractTypeData?.id,
        name: contractTypeData?.attributes?.name,
        description: contractTypeData?.attributes?.description,
        validate_type: contractTypeData?.attributes?.validate_type,
        validate_id: contractTypeData?.attributes?.validate_id,
        validate_sms: contractTypeData?.attributes?.validate_sms,
        validate_picture: contractTypeData?.attributes?.validate_picture,
        validate_biometric: contractTypeData?.attributes?.validate_biometric,
        send_email: contractTypeData?.attributes?.send_email,
        is_active: contractTypeData?.attributes?.is_active,
      }
      return contractType
    }));
  }

  destroyContractType(id: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.delete(`${this.apiURL}/contract_types/${id}`, options);
  }

  // CRUD Contracts

  getContracts(data: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.get(`${this.apiURL}/contracts?${this.parseQueryParams(data)}`, options)
    .pipe(catchError(this.errorHandler))
    .pipe(map((contractsData: any) => {
      const contracts: Contract[] = [];
      contractsData.data.forEach((contractData: any) => {
        const contract: Contract = {
          id: contractData?.id,
          settlement_id: contractData?.attributes?.settlement_id,
          unit_id: contractData?.attributes?.unit_id,
          sign_date: contractData?.attributes?.sign_date,
          expiration_date: contractData?.attributes?.expiration_date,
          status: contractData?.attributes?.status,
          tenant_signatory_id: contractData?.attributes?.tenant_signatory_id,
          contractor_signatory_id: contractData?.attributes?.contractor_signatory_id,
          document_original_url: contractData?.attributes?.document_original_url,
          document_signed_url: contractData?.attributes?.document_signed_url,
          contract_type_id: contractData?.attributes?.contract_type_id,
          settlement: contractData?.attributes?.settlement,
          unit: contractData?.attributes?.unit,
          profile: contractData?.attributes?.profile,
          contract_type: contractData?.attributes?.contract_type
        }
        contracts.push(contract);
      });
      const meta: any = {
        per_page: contractsData?.meta?.params?.per_page,
        total_entries: contractsData?.meta?.pagination?.total_entries,
        current_page: contractsData?.meta?.pagination?.current_page,
      };
      return { data: contracts, meta: meta };
    }
    ));
  }

  getContract(id: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.get(`${this.apiURL}/contracts/${id}`, options)
    .pipe(catchError(this.errorHandler))
    .pipe(map((contractData: any) => {
      const contract: Contract = {
        id: contractData?.data?.id,
        settlement_id: contractData?.data?.attributes?.settlement_id,
        unit_id: contractData?.data?.attributes?.unit_id,
        sign_date: contractData?.data?.attributes?.sign_date,
        expiration_date: contractData?.data?.attributes?.expiration_date,
        status: contractData?.data?.attributes?.status,
        tenant_signatory_id: contractData?.data?.attributes?.tenant_signatory_id,
        contractor_signatory_id: contractData?.data?.attributes?.contractor_signatory_id,
        document_original_url: contractData?.data?.attributes?.document_original_url,
        document_signed_url: contractData?.data?.attributes?.document_signed_url,
        contract_type_id: contractData?.data?.attributes?.contract_type_id,
        settlement: contractData?.data?.attributes?.settlement,
        unit: contractData?.data?.attributes?.unit,
        profile: contractData?.data?.attributes?.profile,
        contract_type: contractData?.data?.attributes?.contract_type
      }
      return contract;
    }));
  }

  createContract(data: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.post(`${this.apiURL}/contracts`, data, options)
    .pipe(catchError(this.errorHandler))
    .pipe(map((contractData: any) => {
      const contract: Contract = {
        id: contractData?.id,
        settlement_id: contractData?.attributes?.settlement_id,
        unit_id: contractData?.attributes?.unit_id,
        sign_date: contractData?.attributes?.sign_date,
        expiration_date: contractData?.attributes?.expiration_date,
        status: contractData?.attributes?.status,
        tenant_signatory_id: contractData?.attributes?.tenant_signatory_id,
        contractor_signatory_id: contractData?.attributes?.contractor_signatory_id,
        document_original_url: contractData?.attributes?.document_original_url,
        document_signed_url: contractData?.attributes?.document_signed_url,
        contract_type_id: contractData?.attributes?.contract_type_id,
        settlement: contractData?.attributes?.settlement,
        unit: contractData?.attributes?.unit,
        contract_type: contractData?.attributes?.contract_type
      }
      return contract;
    }));
  }

  updateContract(id: any, data: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.put(`${this.apiURL}/contracts/${id}`, data, options)
    .pipe(catchError(this.errorHandler))
    .pipe(map((contractData: any) => {
      const contract: Contract = {
        id: contractData?.id,
        settlement_id: contractData?.attributes?.settlement_id,
        unit_id: contractData?.attributes?.unit_id,
        sign_date: contractData?.attributes?.sign_date,
        expiration_date: contractData?.attributes?.expiration_date,
        status: contractData?.attributes?.status,
        tenant_signatory_id: contractData?.attributes?.tenant_signatory_id,
        contractor_signatory_id: contractData?.attributes?.contractor_signatory_id,
        document_original_url: contractData?.attributes?.document_original_url,
        document_signed_url: contractData?.attributes?.document_signed_url,
        contract_type_id: contractData?.attributes?.contract_type_id,
        settlement: contractData?.attributes?.settlement,
        unit: contractData?.attributes?.unit,
        contract_type: contractData?.attributes?.contract_type
      }
      return contract;
    }));
  }

  destroyContract(id: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.delete(`${this.apiURL}/contracts/${id}`, options);
  }

  openSignedContract(id: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.get(`${this.apiURL}/contracts/download_signed_contract/${id}`, options);
  }

  // CRUD Public Records

  getPublicRecords(data: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.get(`${this.apiURL}/public_records?${this.parseQueryParams(data)}`, options);
  }

  getPublicRecord(id: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.get(`${this.apiURL}/public_records/${id}`, options);
  }

  createPublicRecord(data: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.post(`${this.apiURL}/public_records`, data, options);
  }

  updatePublicRecord(id: any, data: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.put(`${this.apiURL}/public_records/${id}`, data, options);
  }

  destroyPublicRecord(id: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.delete(`${this.apiURL}/public_records/${id}`, options);
  }

  // REPORTES

  getContratos(data: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.get(`${this.apiURL}/settlements/contracts?${this.parseQueryParams(data)}`, options);
  }

  downloadTransactions(data: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.post(`${this.apiURL}/transactions/download`, data, options);
  }
  
  getVideo(data: any): Observable<any> {
    return this.httpClient.get(`${this.videoURL}/videos/zazz${data}.mp4`);
  }

  // CRUD Historic consumptions

  getHistoricConsumptions(data: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.get(`${this.apiURL}/historic_consumptions?${this.parseQueryParams(data)}`, options);
  }

  getHistoricConsumption(id: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.get(`${this.apiURL}/historic_consumptions/${id}`, options);
  }

  createHistoricConsumption(data: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.post(`${this.apiURL}/historic_consumptions`, data, options);
  }

  updateHistoricConsumption(id: any, data: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.put(`${this.apiURL}/historic_consumptions/${id}`, data, options);
  }

  destroyHistoricConsumption(id: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.delete(`${this.apiURL}/historic_consumptions/${id}`, options);
  }

  getTenantRegions(id: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.get(`${this.apiURL}/tenants/search_regions/${id}`, options);
  }

  getTenantUnits(id: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.get(`${this.apiURL}/tenants/units/${id}`, options);
  }

  getThsmAddressId(id: any, data: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.put(`${this.apiURL}/tenants/thsm_address/${id}`, data, options);
  }

  getUnitStatement(id: any) {
    const options = this.getHeaders(this.token);
    return this.httpClient.get(`${this.apiURL}/units/statement/${id}`, options);
  }

  getPermissions(data: any): Observable<any> {
    const options = this.getHeaders(this.token);
    return this.httpClient.get(`${this.apiURL}/permissions?${this.parseQueryParams(data)}`, options)
    .pipe(catchError(this.errorHandler))
    .pipe(map((permissionsData: any) => {
      const permissions: Permission[] = [];
      permissionsData.data.forEach((permissionData: any) => {
        const permission: Permission = {
          id: permissionData?.id,
          name: permissionData?.attributes?.name,
          description: permissionData?.attributes?.description,
          group: permissionData?.attributes?.group,
          tipo: permissionData?.attributes?.tipo,
          sub_group: permissionData?.attributes?.sub_group,
        }
        permissions.push(permission);
      });
      const meta: any = {
        per_page: permissionsData?.meta?.params?.per_page,
        total_entries: permissionsData?.meta?.pagination?.total_entries,
        current_page: permissionsData?.meta?.pagination?.current_page,
      };
      return { data: permissions, meta: meta };
    }));
  }

  getSettlementUsers(settlementId: number): Observable<SettlementUser[]> {
    return this.httpClient.get<SettlementUser[]>(`${this.apiURL}/settlements/${settlementId}/users`);
  }

  createSettlementUser(settlementId: number, data: { user: { name: string, last_name: string, phone: string, email: string }, role: string }): Observable<SettlementUser> {
    return this.httpClient.post<SettlementUser>(`${this.apiURL}/settlements/${settlementId}/users`, data);
  }

  updateSettlementUser(settlementId: number, userId: number, data: { role: string }): Observable<SettlementUser> {
    return this.httpClient.put<SettlementUser>(`${this.apiURL}/settlements/${settlementId}/users/${userId}`, data);
  }

  deleteSettlementUser(settlementId: number, userId: number): Observable<any> {
    return this.httpClient.delete(`${this.apiURL}/settlements/${settlementId}/users/${userId}`);
  }

  getGasMeterTypes(): Observable<GasMeterType[]> {
    const options = this.getHeaders(this.token);
    return this.http.get<GasMeterType[]>(`${this.apiURL}/gas_meter_types`, options);
  }

  getGasMeterStatuses(): Observable<GasMeterStatus[]> {
    const options = this.getHeaders(this.token);
    return this.http.get<GasMeterStatus[]>(`${this.apiURL}/gas_meter_statuses`, options);
  }

  getGasMeterBrands(): Observable<GasMeterBrand[]> {
    const options = this.getHeaders(this.token);
    return this.http.get<GasMeterBrand[]>(`${this.apiURL}/gas_meter_brands`, options);
  }

  getGasMeterTypeBrands(data: { gas_meter_type_id?: number | string; gas_meter_brand_id?: number | string } = {}): Observable<GasMeterTypeBrandRelation[]> {
    const options = this.getHeaders(this.token);
    const filteredParams = Object.fromEntries(
      Object.entries(data).filter(([, value]) => value !== null && value !== undefined && `${value}` !== '')
    );
    const query = Object.keys(filteredParams).length > 0
      ? `?${this.parseQueryParams(filteredParams)}`
      : '';
    return this.http.get<GasMeterTypeBrandRelation[]>(`${this.apiURL}/gas_meter_type_brands${query}`, options);
  }

  getGasMeterTypeBrand(id: number | string): Observable<GasMeterTypeBrandRelation> {
    const options = this.getHeaders(this.token);
    return this.http.get<GasMeterTypeBrandRelation>(`${this.apiURL}/gas_meter_type_brands/${id}`, options);
  }

  getgasMeterImage(id: string): Observable<GasMeterDetails> {
    const options = this.getHeaders(this.token);
    return this.http.get<GasMeterDetails>(`${this.apiURL}/gas_meters/${id}`, options);
  }

  deleteGasMeterImage(id: number) {
    const options = this.getHeaders(this.token);
    return this.http.delete<any>(`${this.apiURL}/gas_meters/${id}/image`, options);
  }
  
}
